import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDraggableReorder } from "@/lib/useDraggableReorder";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { cacheGet, cachePut, enqueue, isNetworkError } from "@/lib/offline";
import { toast } from "sonner";
import { checkSpelling } from "@/lib/spellcheck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Trash2,
  Plus,
  Package,
  FileText,
  Hammer,
  ArrowLeft,
  Save,
  Printer,
  CheckCircle2,
  Send,
  XCircle,
  RotateCcw,
  Search as SearchIcon,
  ImageIcon,
  Upload,
  FileImage,
  SpellCheck,
  GripVertical,
  History,
  Mail,
  Eye,
  Link2,
  FileSpreadsheet,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heading as HeadingIcon,
  SeparatorHorizontal,
  RefreshCw,
} from "lucide-react";
import { downloadApiFile } from "@/lib/downloadAuthedFile";
import { formatMoney, STATUS_META, formatDateTime } from "@/lib/format";
import { imageSrc } from "@/pages/Products";
import { useAuthedImage } from "@/lib/useAuthedImage";
import { useCanEdit } from "@/lib/permissions";
import ViewOnlyBanner from "@/components/ViewOnlyBanner";
import QuoteVersionsDrawer from "@/components/QuoteVersionsDrawer";
import EmailProposalDialog from "@/components/EmailProposalDialog";
import ShareLinkDialog from "@/components/ShareLinkDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CATEGORIES = ["Materials", "Labor", "Parts", "Subcontract", "Other"];
const CURRENCIES = ["AUD", "USD", "NZD", "EUR"];

// Optional extras carry cost + markup% like line items; the client-facing
// unit price is always derived from them.
const extraUnitPrice = (x) => {
  const cost = Number(x.cost) || 0;
  const markup = Number(x.markup) || 0;
  return Math.round(cost * (1 + markup / 100) * 100) / 100;
};

function emptyLine() {
  return {
    id: Math.random().toString(36).slice(2),
    kind: "item",
    description: "",
    category: "Materials",
    quantity: 1,
    unit: "ea",
    cost_price: 0,
    // Default 15% margin per user spec — the "Apply to all" bulk
    // control in the header lets the admin change it fleet-wide, and
    // each row's Margin cell can still be edited individually.
    margin_pct: 15,
    unit_price: 0,
    cost_price_high: 0,
    unit_price_high: 0,
  };
}

function emptyHeading() {
  return {
    id: Math.random().toString(36).slice(2),
    kind: "heading",
    description: "",
    category: "Materials",
    quantity: 0,
    unit: "",
    cost_price: 0,
    margin_pct: 0,
    unit_price: 0,
    cost_price_high: 0,
    unit_price_high: 0,
  };
}

// Compute the sell (unit_price) from cost + markup.
// Markup formula: `sell = cost * (1 + markup/100)` — the tradie's
// "add X% to cost" mental model. NOT the accountant's margin
// (which is profit ÷ sell). Field name kept as `margin_pct` for
// DB backward compat with existing quotes; UI labels say "Markup".
function sellFromCostMargin(cost, marginPct) {
  const c = Number(cost) || 0;
  const m = Number(marginPct) || 0;
  return Math.round(c * (1 + m / 100) * 100) / 100;
}

// Locale-aware number formatter used by the Cost input below. Falls
// back to a plain number.toString() if Intl throws.
function formatWithCommas(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return String(n);
  }
}

// Numeric text input that displays "1,400,000" when blurred and the
// raw editable digits ("1400000") while focused. Storing the plain
// number externally keeps the totals maths untouched — we only ever
// swap the *display* string.
function NumberInputWithCommas({ value, onChange, ...rest }) {
  const [focused, setFocused] = useState(false);
  // Raw string while focused so the user can type freely (including a
  // trailing "." mid-edit); formatted string with thousand separators
  // otherwise. An empty raw value renders as "" (not "0") so the
  // placeholder can shine through if the caller sets one.
  const raw = value === "" || value === null || value === undefined ? "" : String(value);
  const display = focused ? raw : formatWithCommas(value);
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        // Accept digits, one decimal point, and commas (we strip
        // commas). Anything else is discarded silently so paste-from-
        // spreadsheet ($1,400,000.00) still lands cleanly.
        const cleaned = e.target.value.replace(/[^0-9.]/g, "");
        onChange({ target: { value: cleaned } });
      }}
      {...rest}
    />
  );
}

export default function QuoteEditor() {
  const canEdit = useCanEdit();
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [products, setProducts] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionCount, setVersionCount] = useState(0);
  const [emailOpen, setEmailOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteQuote = async () => {
    setDeleting(true);
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Quote deleted");
      navigate("/quotes");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
      setDeleting(false);
    }
  };

  const [form, setForm] = useState({
    title: "",
    client_id: null,
    attention_contact_id: null,
    vessel_id: null,
    currency: "AUD",
    gst_enabled: true,
    gst_rate: 0.10,
    alt_currencies: [],
    status: "draft",
    notes: "",
    valid_until: "",
    line_items: [emptyLine()],
    number: "",
    // Proposal PDF content — populated on load, empty = falls back to
    // the Settings defaults at render time.
    intro_letter: "",
    payment_terms_override: "",
    terms_override: "",
    about_sos_override: "",
    about_variant: "sos",
    include_about_sos: true,
    include_yachts_page: false,
    include_shipyard_page: false,
    include_renders_page: false,
    include_letter_page: true,
    include_options_page: true,
    include_project_page: true,
    include_pricing_page: true,
    include_details_page: true,
    include_ga_page: true,
    cover_image_path: "",
    cover_no_image: false,
    vessel_options: [],
    custom_pages: [],
    exclusions_selected: [],
    exclusions_custom: "",
    optional_extras: [],
    price_options: [],
    attachments: [],
    ga_path: "",
    ga_preview_path: "",
    assigned_to: null,
    signatory_id: null,
  });
  const [settings, setSettings] = useState(null);
  const [spellChecking, setSpellChecking] = useState(false);
  const [spellIssues, setSpellIssues] = useState(null);

  const fetchAll = useCallback(async () => {
    // Promise.allSettled so a staff editor without the `clients` or
    // `vessels` permission can still load the page — the missing list
    // just renders as empty in the dropdown rather than blanking the
    // entire quote editor with a 403.
    const [c, v, p, s, a] = await Promise.allSettled([
      api.get("/clients"),
      api.get("/vessels"),
      api.get("/products"),
      api.get("/settings"),
      api.get("/quote-assignees"),
    ]);
    if (c.status === "fulfilled") setClients(c.value.data);
    if (v.status === "fulfilled") setVessels(v.value.data);
    if (p.status === "fulfilled") setProducts(p.value.data);
    if (s.status === "fulfilled") setSettings(s.value.data);
    if (a.status === "fulfilled") setAssignees(a.value.data);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const selectedVessel = vessels.find((v) => v.id === form.vessel_id) || null;

  // Marker suffix identifying the auto-added vessel price line so
  // switching vessels swaps it instead of stacking duplicates.
  const VESSEL_PRICE_MARKER = " — vessel base price";

  // Builds the next form state with the vessel's base price inserted as
  // the top line item (+ currency switch note). Shared by the vessel
  // select handler and the "Add vessel price" inline prompt.
  const insertVesselPrice = (baseForm, vessel) => {
    const price = Number(vessel?.price) || 0;
    // Drop any previous auto-added vessel price line, and the untouched
    // blank starter row, then put this vessel's price at the top.
    const kept = baseForm.line_items.filter(
      (li) =>
        !(li.description || "").endsWith(VESSEL_PRICE_MARKER) &&
        !(li.kind !== "heading" && !(li.description || "").trim() && !Number(li.cost_price) && !Number(li.unit_price)),
    );
    const next = {
      ...baseForm,
      line_items: [
        {
          id: Math.random().toString(36).slice(2),
          kind: "item",
          description: `${vessel.name}${VESSEL_PRICE_MARKER}`,
          category: "Other",
          quantity: 1,
          unit: "ea",
          cost_price: 0,
          margin_pct: 0,
          unit_price: price,
        },
        ...kept,
      ],
    };
    let note = `Added ${vessel.name} base price ${formatMoney(price, vessel.price_currency || baseForm.currency)} as a line item`;
    if (vessel.price_currency && vessel.price_currency !== baseForm.currency) {
      next.currency = vessel.price_currency;
      note += ` — currency switched to ${vessel.price_currency} to match`;
    }
    return { next, note };
  };

  const hasVesselPriceLine = form.line_items.some((li) =>
    (li.description || "").endsWith(VESSEL_PRICE_MARKER),
  );

  const onVesselSelect = (raw) => {
    const vesselId = raw === "none" ? null : raw;
    const vessel = vesselId ? vessels.find((x) => x.id === vesselId) : null;
    if (!vessel || !(Number(vessel.price) > 0)) {
      setForm({ ...form, vessel_id: vesselId });
      return;
    }
    const alreadyThere = form.line_items.some(
      (li) => li.description === `${vessel.name}${VESSEL_PRICE_MARKER}`,
    );
    const { next, note } = insertVesselPrice({ ...form, vessel_id: vesselId }, vessel);
    setForm(next);
    if (!alreadyThere) toast.success(note);
  };

  const addVesselPriceNow = () => {
    if (!selectedVessel) return;
    const { next, note } = insertVesselPrice(form, selectedVessel);
    setForm(next);
    toast.success(note);
  };

  useEffect(() => {
    if (isNew) return;
    let active = true;
    setLoading(true);
    const applyData = (data) =>
        setForm({
          title: data.title || "",
          client_id: data.client_id || null,
          attention_contact_id: data.attention_contact_id || null,
          vessel_id: data.vessel_id || null,
          currency: data.currency || "AUD",
          gst_enabled: !!data.gst_enabled,
          gst_rate: data.gst_rate ?? 0.10,
          alt_currencies: data.alt_currencies || [],
          status: data.status || "draft",
          sent_at: data.sent_at || null,
          last_emailed_at: data.last_emailed_at || null,
          last_emailed_to: data.last_emailed_to || [],
          last_viewed_at: data.last_viewed_at || null,
          view_events: data.view_events || [],
          recipient_links: data.recipient_links || [],
          notes: data.notes || "",
          valid_until: data.valid_until || "",
          line_items: data.line_items?.length
            ? data.line_items.map((li) => ({
                // Legacy quotes pre-date these fields — fall back so the
                // editor never renders `undefined` in a numeric input.
                kind: li.kind === "heading" ? "heading" : "item",
                cost_price: li.cost_price ?? 0,
                margin_pct: li.margin_pct ?? 15,
                cost_price_high: li.cost_price_high ?? 0,
                unit_price_high: li.unit_price_high ?? 0,
                ...li,
              }))
            : [emptyLine()],
          number: data.number || "",
          intro_letter: data.intro_letter || "",
          payment_terms_override: data.payment_terms_override || "",
          terms_override: data.terms_override || "",
          about_sos_override: data.about_sos_override || "",
          about_variant: data.about_variant || "sos",
          include_about_sos: data.include_about_sos ?? data.about_variant !== "yachts",
          include_yachts_page: data.include_yachts_page ?? data.about_variant === "yachts",
          include_shipyard_page: !!data.include_shipyard_page,
          include_renders_page: !!data.include_renders_page,
          include_letter_page: data.include_letter_page !== false,
          include_options_page: data.include_options_page !== false,
          include_project_page: data.include_project_page !== false,
          include_pricing_page: data.include_pricing_page !== false,
          include_details_page: data.include_details_page !== false,
          include_ga_page: data.include_ga_page !== false,
          cover_image_path: data.cover_image_path || "",
          cover_no_image: !!data.cover_no_image,
          vessel_options: (data.vessel_options || []).map((o) => ({
            id: o.id || Math.random().toString(36).slice(2),
            vessel_id: o.vessel_id || "",
            label: o.label || "",
            price_override: o.price_override ?? "",
            specs: o.specs || [],
            features: o.features || [],
          })),
          custom_pages: (data.custom_pages || []).map((p) => ({
            id: p.id || Math.random().toString(36).slice(2),
            title: p.title || "",
            subtitle: p.subtitle || "",
            body: p.body || "",
            position: p.position || "before_pricing",
            image_path: p.image_path || "",
            image_preview_path: p.image_preview_path || "",
            image_preview_paths: p.image_preview_paths || [],
            content_blocks: p.content_blocks || [],
          })),
          exclusions_selected: data.exclusions_selected || [],
          exclusions_custom: data.exclusions_custom || "",
          optional_extras: (data.optional_extras || []).map((x) => ({
            description: x.description || "",
            cost: x.cost ?? x.price ?? "",
            markup: x.markup ?? "",
            price: x.price ?? "",
            qty: x.qty ?? 1,
          })),
          price_options: (data.price_options || []).map((o) => ({
            kind: o.kind === "heading" ? "heading" : "option",
            label: o.label || "",
            description: o.description || "",
            price: o.price ?? "",
            is_default: !!o.is_default,
          })),
          attachments: (data.attachments || []).map((a) => ({
            id: a.id || `att-${Math.random().toString(36).slice(2, 9)}`,
            title: a.title || "",
            file_path: a.file_path || "",
            preview_paths: a.preview_paths || [],
            position: a.position || "end",
          })),
          ga_path: data.ga_path || "",
          ga_preview_path: data.ga_preview_path || "",
          assigned_to: data.assigned_to || null,
          signatory_id: data.signatory_id || null,
        });
    api
      .get(`/quotes/${id}`)
      .then(({ data }) => {
        cachePut(`quote:${id}`, data);
        if (!active) return;
        applyData(data);
      })
      .catch(async (e) => {
        if (isNetworkError(e)) {
          const cached = await cacheGet(`quote:${id}`);
          if (cached && active) {
            applyData(cached);
            toast.message("Offline — showing the last saved copy of this quote");
            return;
          }
        }
        toast.error(formatApiError(e.response?.data?.detail));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id, isNew]);

  // Load version count so the toolbar can show `Versions (N)` — cheap
  // (metadata-only endpoint) but skipped for new quotes since they have
  // no id to query yet.
  const refreshVersionCount = useCallback(() => {
    if (!id) return;
    api
      .get(`/quotes/${id}/versions`)
      .then(({ data }) => setVersionCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {
        /* silently ignore — the drawer surfaces its own error */
      });
  }, [id]);

  useEffect(() => {
    refreshVersionCount();
  }, [refreshVersionCount]);

  // --- Vessel options (comparison page) helpers -------------------------
  const updateOption = (i, patch) => {
    setForm((f) => ({
      ...f,
      vessel_options: f.vessel_options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)),
    }));
  };
  const addOption = () => {
    setForm((f) => ({
      ...f,
      vessel_options: [
        ...f.vessel_options,
        { id: Math.random().toString(36).slice(2), vessel_id: "", label: "", price_override: "", specs: [], features: [] },
      ],
    }));
  };
  const removeOption = (i) => {
    setForm((f) => ({
      ...f,
      vessel_options: f.vessel_options.filter((_, idx) => idx !== i),
    }));
  };
  const optionPickVessel = (i, vid) => {
    const v = vessels.find((x) => x.id === vid);
    updateOption(i, {
      vessel_id: vid,
      label: v?.name || "",
      specs: (v?.principal_particulars || [])
        .filter((r) => (r.kind || "row") !== "heading" && r.enabled !== false && (r.label || r.value))
        .map((r) => ({ label: r.label || "", value: r.value || "" })),
    });
  };

  const updateCustomPage = (i, patch) => {
    setForm((f) => ({
      ...f,
      custom_pages: f.custom_pages.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
    }));
  };
  // Custom page artwork upload — one hidden input shared across pages;
  // `customPageUploadIdx` remembers which page the file belongs to.
  const customPageFileRef = useRef(null);
  const customPageUploadIdx = useRef(null);
  const [customPageUploading, setCustomPageUploading] = useState(false);
  const onCustomPageFile = async (e) => {
    const file = e.target.files?.[0];
    const idx = customPageUploadIdx.current;
    if (!file || idx == null) return;
    setCustomPageUploading(true);
    try {
      const isPdf =
        /\.pdf$/i.test(file.name || "") || file.type === "application/pdf";
      const isDocx =
        /\.docx$/i.test(file.name || "") ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      if (isPdf || isDocx) {
        // Documents — extract text/tables/images so the page renders in native SOS style.
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/extract", fd);
        let blocks = data.blocks || [];
        if (blocks.length > 0) {
          const pg = form.custom_pages[idx] || {};
          const patch = {
            content_blocks: blocks,
            image_path: "",
            image_preview_path: "",
            image_preview_paths: [],
          };
          if (!pg.title && data.title) {
            patch.title = data.title;
            if (blocks[0]?.type === "heading" && blocks[0].text === data.title) {
              patch.content_blocks = blocks.slice(1);
            }
          }
          updateCustomPage(idx, patch);
          toast.success(
            `Extracted ${patch.content_blocks.length} sections — review and edit below`,
          );
          return;
        }
        if (isDocx) {
          toast.error("No content could be extracted from this Word document");
          return;
        }
        toast.message(
          "No text could be extracted — including the pages as images instead",
        );
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      updateCustomPage(idx, {
        content_blocks: [],
        image_path: data.path,
        image_preview_path: data.preview_path || data.path,
        image_preview_paths:
          data.preview_paths && data.preview_paths.length
            ? data.preview_paths
            : [data.preview_path || data.path],
      });
      toast.success(
        data.preview_paths && data.preview_paths.length > 1
          ? `Uploaded — ${data.preview_paths.length} pages will be included`
          : "Page artwork uploaded",
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setCustomPageUploading(false);
      if (customPageFileRef.current) customPageFileRef.current.value = "";
    }
  };
  const updateBlock = (pi, bi, patch) =>
    updateCustomPage(pi, {
      content_blocks: (form.custom_pages[pi]?.content_blocks || []).map((b, i) =>
        i === bi ? { ...b, ...patch } : b,
      ),
    });
  const removeBlock = (pi, bi) =>
    updateCustomPage(pi, {
      content_blocks: (form.custom_pages[pi]?.content_blocks || []).filter(
        (_, i) => i !== bi,
      ),
    });
  const moveBlock = (pi, bi, dir) => {
    const blocks = [...(form.custom_pages[pi]?.content_blocks || [])];
    const j = bi + dir;
    if (j < 0 || j >= blocks.length) return;
    [blocks[bi], blocks[j]] = [blocks[j], blocks[bi]];
    updateCustomPage(pi, { content_blocks: blocks });
  };

  // Live FX rates (Frankfurter/ECB daily reference) keyed by base currency.
  const [fxData, setFxData] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const fetchFx = async (base) => {
    setFxLoading(true);
    try {
      const { data } = await api.get("/fx-rates", { params: { base } });
      setFxData(data);
      return data;
    } catch {
      toast.error("Couldn't fetch live exchange rates — enter the rate manually.");
      return null;
    } finally {
      setFxLoading(false);
    }
  };
  const applyLiveRate = async (cur) => {
    const data =
      fxData && fxData.base === form.currency ? fxData : await fetchFx(form.currency);
    const rate = data?.rates?.[cur];
    if (!rate) return;
    setForm((f) => ({
      ...f,
      alt_currencies: (f.alt_currencies || []).map((a) =>
        a.currency === cur ? { ...a, rate: String(Number(rate.toFixed(4))) } : a,
      ),
    }));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let subtotalHigh = 0;
    let hasRange = false;
    for (const li of form.line_items) {
      if ((li.kind || "item") === "heading") continue;
      const qty = Number(li.quantity) || 0;
      const low = Number(li.unit_price) || 0;
      const high = Number(li.unit_price_high) || 0;
      subtotal += qty * low;
      subtotalHigh += qty * (high > 0 ? high : low);
      if (high > 0) hasRange = true;
    }
    const gst = form.gst_enabled ? subtotal * (Number(form.gst_rate) || 0) : 0;
    return { subtotal, subtotalHigh, hasRange, gst, total: subtotal + gst };
  }, [form.line_items, form.gst_enabled, form.gst_rate]);

  // Section subtotals — for every heading-led group of item rows, we
  // render a bold "…subtotal" row directly below the LAST item in that
  // group (both in this editor and on the PDF). `sectionSubtotalAfter[i]`
  // is { label, amount } when a subtotal band should render below row i,
  // otherwise null. Items that appear BEFORE any heading (i.e. an
  // ungrouped preamble) are excluded — they'd just duplicate the grand
  // subtotal.
  const sectionSubtotalAfter = useMemo(() => {
    const out = new Array(form.line_items.length).fill(null);
    let currentHeading = null;
    let runningSum = 0;
    let runningSumHigh = 0;
    let lastItemIdx = -1;
    const flush = () => {
      if (currentHeading && lastItemIdx >= 0 && runningSum > 0) {
        out[lastItemIdx] = {
          label: currentHeading,
          amount: runningSum,
          amountHigh: runningSumHigh > runningSum ? runningSumHigh : 0,
        };
      }
      currentHeading = null;
      runningSum = 0;
      runningSumHigh = 0;
      lastItemIdx = -1;
    };
    form.line_items.forEach((li, i) => {
      if ((li.kind || "item") === "heading") {
        flush();
        currentHeading = li.description || "Section";
      } else if (currentHeading) {
        const qty = Number(li.quantity) || 0;
        const low = Number(li.unit_price) || 0;
        const high = Number(li.unit_price_high) || 0;
        runningSum += qty * low;
        runningSumHigh += qty * (high > 0 ? high : low);
        lastItemIdx = i;
      }
    });
    flush();
    return out;
  }, [form.line_items]);

  const updateLine = (i, patch) => {
    setForm((f) => {
      const items = [...f.line_items];
      const next = { ...items[i], ...patch };
      // Auto-recompute sell price when cost or margin changes on an
      // item row. Heading rows carry no numeric data so we skip.
      if (
        (next.kind || "item") !== "heading" &&
        ("cost_price" in patch || "margin_pct" in patch || "cost_price_high" in patch)
      ) {
        next.unit_price = sellFromCostMargin(next.cost_price, next.margin_pct);
        next.unit_price_high = Number(next.cost_price_high)
          ? sellFromCostMargin(next.cost_price_high, next.margin_pct)
          : 0;
      }
      items[i] = next;
      return { ...f, line_items: items };
    });
  };
  const removeLine = (i) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.filter((_, idx) => idx !== i),
    }));
  };
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  const addHeading = () => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyHeading()] }));

  // Native HTML5 drag-and-drop reorder for line_items — same hook used
  // on the Vessels PP editor. Grabs anywhere along the drag handle in
  // col 1 and lets the admin drop above/below any other row (items OR
  // headings), so line items can be moved into a section by dragging
  // them under the relevant heading.
  const lineReorder = useDraggableReorder(
    form.line_items,
    (next) =>
      setForm((f) => ({
        ...f,
        line_items: typeof next === "function" ? next(f.line_items) : next,
      })),
  );

  // Bulk margin controls — one input + Apply-to-all button above the
  // table. Applying overwrites every item row's margin (headings are
  // skipped) and re-back-solves the sell price from the new margin.
  const [bulkMargin, setBulkMargin] = useState(15);
  const applyBulkMargin = () => {
    const m = Number(bulkMargin);
    if (Number.isNaN(m)) {
      toast.error("Markup must be a number");
      return;
    }
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li) => {
        if ((li.kind || "item") === "heading") return li;
        const nextMargin = m;
        return {
          ...li,
          margin_pct: nextMargin,
          unit_price: sellFromCostMargin(li.cost_price, nextMargin),
          unit_price_high: Number(li.cost_price_high)
            ? sellFromCostMargin(li.cost_price_high, nextMargin)
            : 0,
        };
      }),
    }));
    toast.success(`Markup set to ${m}% on all line items`);
  };

  // File import — POSTs the picked file to /api/quotes/import-lines
  // and either replaces or appends the resulting rows depending on
  // whether the current form is empty.
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/quotes/import-lines", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imported = data.line_items || [];
      if (!imported.length) {
        toast.error("No rows found in the file");
        return;
      }
      setForm((f) => {
        // Replace a lone blank starter row; otherwise append.
        const hasBlankStarter =
          f.line_items.length === 1 &&
          (f.line_items[0].kind || "item") === "item" &&
          !f.line_items[0].description.trim() &&
          Number(f.line_items[0].unit_price || 0) === 0;
        const items = hasBlankStarter ? imported : [...f.line_items, ...imported];
        return { ...f, line_items: items };
      });
      toast.success(`Imported ${imported.length} row${imported.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      // Reset the input so the same file can be re-selected.
      if (importFileRef.current) importFileRef.current.value = "";
    }
  };

  const addFromProduct = (p) => {
    // Catalogue prices are treated as sell prices. Default margin
    // becomes 0 so the "Amount" mirrors the catalogue's price exactly
    // unless the admin edits it. Cost stays 0 so a later margin edit
    // won't accidentally clobber the catalogue price.
    const newItem = {
      id: Math.random().toString(36).slice(2),
      kind: "item",
      description: p.description ? `${p.name} — ${p.description}` : p.name,
      category: p.category || "Materials",
      quantity: 1,
      unit: p.unit || "ea",
      cost_price: 0,
      margin_pct: 0,
      unit_price: Number(p.price) || 0,
    };
    setForm((f) => {
      // Replace blank starter row if present, otherwise append
      const hasBlankStarter =
        f.line_items.length === 1 &&
        !f.line_items[0].description.trim() &&
        Number(f.line_items[0].unit_price || 0) === 0;
      const items = hasBlankStarter ? [newItem] : [...f.line_items, newItem];
      return { ...f, line_items: items };
    });
    toast.success(`Added "${p.name}" to quote`);
  };

  const filteredProducts = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.supplier_name || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q),
    );
  }, [products, pickerQuery]);

  const filteredVessels = useMemo(() => {
    if (!form.client_id) return vessels;
    const matching = vessels.filter((v) => !v.client_id || v.client_id === form.client_id);
    return matching.length ? matching : vessels;
  }, [vessels, form.client_id]);

  const [attachUploading, setAttachUploading] = useState(false);
  // Spec-sheet chooser shown when a picked catalogue product has sheets.
  const [specPick, setSpecPick] = useState(null);
  const [specAdding, setSpecAdding] = useState(false);

  const confirmSpecPick = async (includeSheets) => {
    const { product, checked } = specPick;
    addFromProduct(product);
    setPickerOpen(false);
    if (!includeSheets) {
      setSpecPick(null);
      return;
    }
    const sheets = (product.spec_sheets || []).filter((_, i) => checked[i]);
    setSpecAdding(true);
    let added = 0;
    let skipped = 0;
    for (const s of sheets) {
      if ((form.attachments || []).some((a) => a.file_path === s.path)) {
        skipped += 1;
        continue;
      }
      let previews = [];
      try {
        const { data } = await api.post("/files/previews", { path: s.path });
        previews = data.preview_paths || [];
      } catch {
        /* attach without previews rather than failing the pick */
      }
      setForm((f) => ({
        ...f,
        attachments: [
          ...(f.attachments || []),
          {
            id: `att-${Date.now()}-${added}`,
            title: (s.name || "Spec sheet").replace(/\.[^.]+$/, ""),
            file_path: s.path,
            preview_paths: previews,
            position: "end",
          },
        ],
      }));
      added += 1;
    }
    setSpecAdding(false);
    setSpecPick(null);
    if (added) {
      toast.success(
        `${product.name} added with ${added} spec sheet${added > 1 ? "s" : ""} attached — set where they appear in Attachments, then Save`
      );
    } else if (skipped) {
      toast.success(`${product.name} added — spec sheet already attached to this quote`);
    }
  };

  const addAttachment = async (file) => {
    if (!file) return;
    setAttachUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({
        ...f,
        attachments: [
          ...(f.attachments || []),
          {
            id: `att-${Date.now()}`,
            title: (file.name || "Document").replace(/\.[^.]+$/, ""),
            file_path: data.path,
            preview_paths: data.preview_paths || [],
            position: "end",
          },
        ],
      }));
      toast.success("Attachment added — choose where it appears, then Save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setAttachUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = {
        title: form.title.trim(),
        client_id: form.client_id || null,
        attention_contact_id: form.attention_contact_id || null,
        vessel_id: form.vessel_id || null,
        currency: form.currency,
        gst_enabled: form.gst_enabled,
        gst_rate: Number(form.gst_rate) || 0,
        alt_currencies: (form.alt_currencies || [])
          .filter((a) => a.currency && a.currency !== form.currency && Number(a.rate) > 0)
          .map((a) => ({ currency: a.currency, rate: Number(a.rate) })),
        status: form.status,
        notes: form.notes,
        valid_until: form.valid_until || null,
        line_items: form.line_items.map((li) => ({
          id: li.id,
          kind: (li.kind || "item") === "heading" ? "heading" : "item",
          description: li.description,
          category: li.category,
          quantity: Number(li.quantity) || 0,
          unit: li.unit || "ea",
          cost_price: Number(li.cost_price) || 0,
          margin_pct: Number(li.margin_pct) || 0,
          unit_price: Number(li.unit_price) || 0,
          cost_price_high: Number(li.cost_price_high) || 0,
          unit_price_high: Number(li.unit_price_high) || 0,
        })),
        intro_letter: form.intro_letter || "",
        payment_terms_override: form.payment_terms_override || "",
        terms_override: form.terms_override || "",
        about_sos_override: form.about_sos_override || "",
        about_variant: form.about_variant || "sos",
        include_about_sos: !!form.include_about_sos,
        include_yachts_page: !!form.include_yachts_page,
        include_shipyard_page: !!form.include_shipyard_page,
        include_renders_page: !!form.include_renders_page,
        include_letter_page: form.include_letter_page !== false,
        include_options_page: form.include_options_page !== false,
        include_project_page: form.include_project_page !== false,
        include_pricing_page: form.include_pricing_page !== false,
        include_details_page: form.include_details_page !== false,
        include_ga_page: form.include_ga_page !== false,
        cover_image_path: form.cover_image_path || "",
        cover_no_image: !!form.cover_no_image,
        vessel_options: (form.vessel_options || [])
          .filter((o) => o.vessel_id)
          .map((o) => ({
            id: o.id,
            vessel_id: o.vessel_id,
            label: o.label || "",
            price_override:
              o.price_override === "" || o.price_override == null
                ? null
                : Number(o.price_override),
            specs: (o.specs || []).filter((s) => s.label || s.value),
            features: (o.features || []).map((f) => f.trim()).filter(Boolean),
          })),
        custom_pages: (form.custom_pages || []).filter(
          (p) => p.title || p.body || p.image_path || (p.content_blocks || []).length,
        ),
        exclusions_selected: form.exclusions_selected || [],
        exclusions_custom: form.exclusions_custom || "",
        optional_extras: (form.optional_extras || [])
          .filter((x) => (x.description || "").trim())
          .map((x) => ({
            description: x.description.trim(),
            cost: Number(x.cost) || 0,
            markup: Number(x.markup) || 0,
            price: extraUnitPrice(x),
            qty: Math.max(1, Number(x.qty) || 1),
          })),
        price_options: (form.price_options || [])
          .filter((o) => (o.label || "").trim())
          .map((o) => ({
            kind: o.kind === "heading" ? "heading" : "option",
            label: o.label.trim(),
            description: (o.description || "").trim(),
            price: o.kind === "heading" ? null : Number(o.price) || 0,
            is_default: o.kind === "heading" ? false : !!o.is_default,
          })),
        attachments: (form.attachments || [])
          .filter((a) => a.file_path)
          .map((a) => ({
            id: a.id,
            title: (a.title || "").trim() || "Attachment",
            file_path: a.file_path,
            preview_paths: a.preview_paths || [],
            position: a.position || "end",
          })),
        ga_path: form.ga_path || "",
        ga_preview_path: form.ga_preview_path || "",
        assigned_to: form.assigned_to || null,
        signatory_id: form.signatory_id || null,
      };
    try {
      const { data } = isNew
        ? await api.post("/quotes", payload)
        : await api.put(`/quotes/${id}`, payload);
      toast.success(isNew ? "Quote created" : "Quote saved");
      if (isNew) navigate(`/quotes/${data.id}`, { replace: true });
      else
        setForm((f) => ({
          ...f,
          line_items: data.line_items?.length ? data.line_items : f.line_items,
          number: data.number,
        }));
      refreshVersionCount();
    } catch (e) {
      if (isNetworkError(e)) {
        if (isNew) {
          await enqueue({ type: "create_quote", tempId: `temp-${Date.now()}`, payload });
          toast.success("Saved offline — this quote will be created when you're back online");
          navigate("/quotes");
        } else {
          await enqueue({ type: "update_quote", targetId: id, payload });
          const cached = await cacheGet(`quote:${id}`);
          cachePut(`quote:${id}`, { ...(cached || {}), ...payload });
          toast.success("Saved offline — will sync when you're back online");
        }
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    if (isNew) {
      setForm((f) => ({ ...f, status }));
      return;
    }
    try {
      const { data } = await api.patch(`/quotes/${id}/status`, { status });
      setForm((f) => ({ ...f, status: data.status }));
      toast.success(`Marked as ${STATUS_META[status].label}`);
      refreshVersionCount();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // AI-generated introduction letter. Requires the quote to be saved
  // (backend needs a persisted client_id / vessel_id to resolve first
  // name + vessel specs). The response text is dropped straight into
  // the textarea so the admin can edit before saving.
  const runSpellCheck = async () => {
    setSpellChecking(true);
    try {
      const issues = await checkSpelling(form.intro_letter || "");
      setSpellIssues(issues);
      if (issues.length === 0) toast.success("No spelling issues found");
    } catch {
      toast.error("Could not load the dictionary — please try again");
    } finally {
      setSpellChecking(false);
    }
  };

  const fixSpelling = (word, replacement) => {
    const re = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
    setForm((f) => ({ ...f, intro_letter: (f.intro_letter || "").replace(re, replacement) }));
    setSpellIssues((iss) => (iss || []).filter((x) => x.word !== word));
  };

  const toggleExclusion = (line) => {
    setForm((f) => {
      const already = (f.exclusions_selected || []).includes(line);
      return {
        ...f,
        exclusions_selected: already
          ? f.exclusions_selected.filter((x) => x !== line)
          : [...(f.exclusions_selected || []), line],
      };
    });
  };

  // GA upload — reuses the shared /api/upload endpoint which returns
  // both the original path AND a PNG preview path (auto-rasterised for
  // PDFs). Print view uses `ga_preview_path` for embedding.
  const gaFileRef = useRef(null);
  const [gaUploading, setGaUploading] = useState(false);
  const onGaFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // NOTE: do NOT set Content-Type manually — axios auto-populates
      // `multipart/form-data; boundary=…` when it sees a FormData body.
      // Forcing the header strips the boundary and the server can't
      // parse the parts, which surfaces to the user as a generic
      // "can't reach the server" toast.
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({
        ...f,
        ga_path: data.path,
        ga_preview_path: data.preview_path || data.path,
      }));
      toast.success("GA uploaded — will show on the last PDF page");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setGaUploading(false);
      if (gaFileRef.current) gaFileRef.current.value = "";
    }
  };
  const removeGa = () =>
    setForm((f) => ({ ...f, ga_path: "", ga_preview_path: "" }));

  // Cover-image override — replaces the vessel render on the cover page.
  const coverFileRef = useRef(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, cover_image_path: data.preview_path || data.path }));
      toast.success("Cover image set — remember to save the quote");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setCoverUploading(false);
      if (coverFileRef.current) coverFileRef.current.value = "";
    }
  };

  // When the upload endpoint fails to rasterise page 1 of a PDF (usually
  // due to memory pressure on a small deploy tier), it silently falls
  // back to `preview_path = path`. This helper re-runs the rasterisation
  // server-side on the current tier so a broken preview can be recovered
  // without re-uploading the file.
  const [regenGa, setRegenGa] = useState(false);
  const needsGaRegen =
    !!form.ga_path &&
    /\.pdf$/i.test(form.ga_path) &&
    (!form.ga_preview_path || form.ga_preview_path === form.ga_path);
  const regenerateGaPreview = async () => {
    setRegenGa(true);
    try {
      const { data } = await api.post(`/quotes/${id}/regenerate-ga-preview`);
      setForm((f) => ({ ...f, ga_preview_path: data.ga_preview_path }));
      toast.success("GA preview regenerated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Regeneration failed");
    } finally {
      setRegenGa(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-[#5B6B7C] font-display tracking-wider text-sm">Loading quote…</div>
    );
  }

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-32">
      {/* Header — flex-wrap so the toolbar drops below the title instead of
          squeezing it into a sliver when all buttons fit on one line. */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="flex-1 basis-[340px] min-w-0">
          <Link
            to="/quotes"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[#5B6B7C] hover:text-[#011836] mb-3"
            data-testid="back-to-quotes"
          >
            <ArrowLeft className="h-3 w-3" /> Back to quotes
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            {form.number && (
              <span className="font-display text-sm text-[#0088CC] font-semibold">
                {form.number}
              </span>
            )}
            <span
              className={`text-xs px-2 py-0.5 rounded ${STATUS_META[form.status]?.className || ""}`}
              data-testid="quote-status-pill"
            >
              {STATUS_META[form.status]?.label || form.status}
            </span>
            {form.last_viewed_at ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 transition-colors"
                    title="Click for the full opens history"
                    data-testid="quote-opened-stamp"
                  >
                    <Eye className="h-3 w-3" />
                    Opened {formatDateTime(form.last_viewed_at)}
                    {(form.view_events || []).length > 1
                      ? ` · ${(form.view_events || []).length} views`
                      : ""}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-96 bg-white border-[#DDE5EC] text-[#011836] p-0 max-h-80 overflow-y-auto"
                  data-testid="quote-opens-history"
                >
                  <div className="px-3 py-2 border-b border-[#DDE5EC] text-[10px] uppercase tracking-widest text-[#5B6B7C]">
                    Opens history — who · when · where
                  </div>
                  {[...(form.view_events || [])].reverse().map((ev, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 border-b border-[#E8EEF3] last:border-b-0"
                      data-testid={`quote-open-event-${i}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-medium text-[#011836] truncate">
                          {ev.recipient || "Shared link"}
                        </span>
                        <span className="text-[10px] text-[#5B6B7C] whitespace-nowrap">
                          {formatDateTime(ev.at)}
                        </span>
                      </div>
                      {ev.location && (
                        <div className="text-[10px] text-[#0088CC] mt-0.5">
                          {ev.location} <span className="text-[#64748B]">(approx.)</span>
                        </div>
                      )}
                    </div>
                  ))}
                </PopoverContent>
              </Popover>
            ) : form.last_emailed_at || form.sent_at ? (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded border border-[#0088CC]/40 bg-[#0088CC]/10 text-[#0088CC]"
                title={
                  (form.last_emailed_to || []).length
                    ? `Sent to ${(form.last_emailed_to || []).join(", ")}`
                    : undefined
                }
                data-testid="quote-sent-stamp"
              >
                <Mail className="h-3 w-3" />
                Sent {formatDateTime(form.last_emailed_at || form.sent_at)} — not yet opened
              </span>
            ) : null}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#011836] mt-2">
            {isNew ? "New Quote" : form.title || "Untitled quote"}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew && (
            <Button
              variant="outline"
              onClick={() => setVersionsOpen(true)}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
              data-testid="versions-drawer-btn"
            >
              <History className="h-4 w-4 mr-2" />
              Versions{versionCount ? ` (${versionCount})` : ""}
            </Button>
          )}
          {!isNew && (
            <Button
              variant="outline"
              onClick={() => window.open(`/quotes/${id}/print`, "_blank")}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
              data-testid="print-quote-btn"
            >
              <Printer className="h-4 w-4 mr-2" /> Export PDF
            </Button>
          )}
          {!isNew && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await downloadApiFile(
                    `/quotes/${id}/export.xlsx`,
                    `SOS-quote-${form.number || id}.xlsx`,
                  );
                  toast.success("Spreadsheet downloaded");
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
                }
              }}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel
            </Button>
          )}
          {!isNew && canEdit && (
            <Button
              variant="outline"
              onClick={() => setEmailOpen(true)}
              className="border-[#0088CC] text-[#0088CC] hover:bg-[#E6F2FA] hover:text-[#011836]"
              data-testid="email-proposal-btn"
            >
              <Mail className="h-4 w-4 mr-2" /> Email proposal
            </Button>
          )}
          {!isNew && (
            <Button
              variant="outline"
              onClick={() => setShareOpen(true)}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
              data-testid="share-link-btn"
            >
              <Link2 className="h-4 w-4 mr-2" /> Share link
            </Button>
          )}
          {!isNew && canEdit && (
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#FDECEC] hover:text-[#EF4444]"
              data-testid="delete-quote-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving}
            className={`bg-[#0088CC] hover:bg-[#006DA6] text-white ${canEdit ? "" : "hidden"}`}
            data-testid="save-quote-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </Button>
        </div>
      </div>

      {/* Status actions */}
      {!isNew && canEdit && (
        <div className="sos-surface rounded-lg p-4 mb-6 flex flex-wrap gap-2 items-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#5B6B7C] mr-2">Pipeline:</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setStatus("draft")}
            className="border-[#C4D0DB] text-[#5B6B7C] hover:text-[#011836]"
            data-testid="status-draft-btn"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Draft
          </Button>
          <Button
            size="sm"
            onClick={() => setStatus("sent")}
            className="bg-[#E6F2FA] border border-[#0088CC] text-[#0088CC] hover:bg-[#E6F2FA]"
            data-testid="status-sent-btn"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" /> Mark Sent
          </Button>
          <Button
            size="sm"
            onClick={() => setStatus("accepted")}
            className="bg-[#E7F6EE] border border-[#10B981] text-[#10B981] hover:bg-[#E7F6EE]"
            data-testid="status-accepted-btn"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Accepted
          </Button>
          <Button
            size="sm"
            onClick={() => setStatus("rejected")}
            className="bg-[#FDECEC] border border-[#EF4444] text-[#EF4444] hover:bg-[#FDECEC]"
            data-testid="status-rejected-btn"
          >
            <XCircle className="h-3.5 w-3.5 mr-1.5" /> Rejected
          </Button>
          {form.status === "accepted" && (
            <Button
              size="sm"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/quotes/${id}/convert-to-project`);
                  toast.success("Project ready");
                  navigate(`/projects/${data.id}`);
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                }
              }}
              className="bg-[#011836] hover:bg-[#0d2b52] text-white"
              data-testid="convert-to-project-btn"
            >
              <Hammer className="h-3.5 w-3.5 mr-1.5" /> Convert to project
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-6">
          <section className="sos-surface rounded-lg p-5">
            <h2 className="font-display text-lg font-semibold text-[#011836] mb-4">Details</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. 22m Patrol Vessel Hull Fabrication"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="quote-title-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Client</Label>
                  <Select
                    value={form.client_id || "none"}
                    onValueChange={(v) =>
                      setForm({ ...form, client_id: v === "none" ? null : v, attention_contact_id: null })
                    }
                  >
                    <SelectTrigger
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                      data-testid="quote-client-select"
                    >
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                      <SelectItem value="none">— No client —</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const cli = clients.find((c) => c.id === form.client_id);
                    const contacts = cli?.contacts || [];
                    if (contacts.length < 2) return null;
                    return (
                      <div className="mt-2">
                        <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Attention</Label>
                        <Select
                          value={form.attention_contact_id || "primary"}
                          onValueChange={(v) =>
                            setForm({ ...form, attention_contact_id: v === "primary" ? null : v })
                          }
                        >
                          <SelectTrigger
                            className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                            data-testid="quote-attention-select"
                          >
                            <SelectValue placeholder="Who is this quote for?" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                            <SelectItem value="primary">
                              — Primary ({cli.primary_contact?.full_name || "default"}) —
                            </SelectItem>
                            {contacts.map((ct) => (
                              <SelectItem key={ct.id} value={ct.id}>
                                {ct.full_name || ct.email}
                                {ct.position ? ` · ${ct.position}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-[#94A3B8] mt-1">
                          The proposal letter and "Attn:" line address this contact.
                        </p>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Vessel</Label>
                  <Select
                    value={form.vessel_id || "none"}
                    onValueChange={onVesselSelect}
                  >
                    <SelectTrigger
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                      data-testid="quote-vessel-select"
                    >
                      <SelectValue placeholder="Select vessel" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                      <SelectItem value="none">— No vessel —</SelectItem>
                      {filteredVessels.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                          {v.vessel_type ? ` · ${v.vessel_type}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Valid until</Label>
                  <Input
                    type="date"
                    value={form.valid_until || ""}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                    className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                    data-testid="quote-valid-until"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Currency</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => setForm({ ...form, currency: v })}
                  >
                    <SelectTrigger
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                      data-testid="quote-currency-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`currency-${c}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Assigned to
                  </Label>
                  <Select
                    value={form.assigned_to || "none"}
                    onValueChange={(v) =>
                      setForm({ ...form, assigned_to: v === "none" ? null : v })
                    }
                  >
                    <SelectTrigger
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                      data-testid="quote-assignee-select"
                    >
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                      <SelectItem value="none" data-testid="quote-assignee-none">
                        — Unassigned —
                      </SelectItem>
                      {assignees.map((u) => (
                        <SelectItem
                          key={u.id}
                          value={u.id}
                          data-testid={`quote-assignee-${u.id}`}
                        >
                          {u.name}
                          {u.role === "admin" ? " · admin" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Signed by
                  </Label>
                  <Select
                    value={form.signatory_id || "none"}
                    onValueChange={(v) =>
                      setForm({ ...form, signatory_id: v === "none" ? null : v })
                    }
                  >
                    <SelectTrigger
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                      data-testid="quote-signatory-select"
                    >
                      <SelectValue placeholder="Default signatory" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                      <SelectItem value="none" data-testid="quote-signatory-default">
                        — Default (Michael Graham-McKenna) —
                      </SelectItem>
                      {assignees.map((u) => (
                        <SelectItem
                          key={u.id}
                          value={u.id}
                          data-testid={`quote-signatory-${u.id}`}
                        >
                          {u.name}
                          {u.role === "admin" ? " · admin" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-[#64748B] mt-1">
                    Whose name signs the proposal's introduction letter.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="sos-surface rounded-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-[#DDE5EC]">
              <h2 className="font-display text-lg font-semibold text-[#011836]">Line Items</h2>
              {canEdit && (
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Bulk margin control — applies to every item row.
                      Headings are excluded automatically. */}
                  <div
                    className="flex items-center gap-1.5 border border-[#C4D0DB] rounded px-2 py-1 bg-white"
                    data-testid="bulk-margin-control"
                  >
                    <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C] whitespace-nowrap">
                      Markup
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={bulkMargin}
                      onChange={(e) => setBulkMargin(e.target.value)}
                      className="w-16 h-7 text-right bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] px-1.5"
                      data-testid="bulk-margin-input"
                    />
                    <span className="text-xs text-[#5B6B7C]">%</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={applyBulkMargin}
                      className="h-7 px-2 border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836] text-xs"
                      data-testid="bulk-margin-apply-btn"
                    >
                      Apply to all
                    </Button>
                  </div>
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                    onChange={onImportFile}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => importFileRef.current?.click()}
                    disabled={importing}
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                    data-testid="import-lines-btn"
                    title="Upload a CSV or XLSX with columns Section | Description | Qty | Unit | Unit Price | Cost Price"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1.5 text-[#0088CC]" />
                    {importing ? "Importing…" : "Import"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPickerOpen(true)}
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                    data-testid="add-from-catalogue-btn"
                  >
                    <Package className="h-3.5 w-3.5 mr-1.5 text-[#0088CC]" /> Add from Catalogue
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={addHeading}
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                    data-testid="add-heading-btn"
                    title="Insert a section heading (label only, no qty/price)"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Heading
                  </Button>
                  <Button
                    size="sm"
                    onClick={addLine}
                    className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                    data-testid="add-line-item-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Item
                  </Button>
                </div>
              )}
            </div>
            {/* Vessel-price nudge — shows when the linked vessel has a
                catalogue price that isn't on the quote yet (e.g. the quote
                was auto-drafted or the vessel was selected before this
                feature shipped, so no select event ever fired). */}
            {canEdit && selectedVessel && Number(selectedVessel.price) > 0 && !hasVesselPriceLine && (
              <div
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-[#0088CC]/25 bg-[#E6F2FA]/50"
                data-testid="vessel-price-nudge"
              >
                <div className="text-sm text-[#5B6B7C]">
                  <span className="text-[#011836] font-medium">{selectedVessel.name}</span> has a
                  catalogue price of{" "}
                  <span className="text-[#0088CC] font-semibold">
                    {formatMoney(selectedVessel.price, selectedVessel.price_currency || form.currency)}
                  </span>{" "}
                  that isn&apos;t on this quote yet.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addVesselPriceNow}
                  className="border-[#0088CC] text-[#0088CC] hover:bg-[#E6F2FA] hover:text-[#011836]"
                  data-testid="add-vessel-price-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add vessel price
                </Button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C] border-b border-[#DDE5EC]">
                    <th className="w-8" aria-label="Drag to reorder" />
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-right px-3 py-2 w-20">Qty</th>
                    {/* Cost column is wider than the others so full
                        numbers (e.g. 1,400,000) fit without truncation.
                        Same font/weight as every other cell — the width
                        alone provides breathing room. */}
                    <th className="text-right px-3 py-2 w-40" title="Cost you pay per unit">Cost</th>
                    <th className="text-right px-3 py-2 w-24" title="Markup percentage — sell price = cost × (1 + markup/100)">Markup %</th>
                    <th className="text-right px-3 py-2 w-28">Amount</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {form.line_items.map((li, i) => {
                    const isHeading = (li.kind || "item") === "heading";
                    const isDragging = lineReorder.draggingIndex === i;
                    const isOver =
                      lineReorder.overIndex === i &&
                      lineReorder.draggingIndex !== null &&
                      lineReorder.draggingIndex !== i;
                    if (isHeading) {
                      // Section heading row — full-width label, no
                      // numeric columns. Mimics the vessel PP heading UX
                      // so admins get the same mental model on both
                      // editors.
                      return (
                        <tr
                          key={li.id}
                          {...lineReorder.rowProps(i)}
                          className={`border-b border-[#DDE5EC] bg-[#E6F2FA]/30 ${
                            isDragging ? "opacity-40" : ""
                          } ${isOver ? "ring-2 ring-[#0088CC]/60" : ""}`}
                          data-testid={`line-item-${i}`}
                          data-kind="heading"
                        >
                          <td className="px-1 py-2 align-middle">
                            <button
                              type="button"
                              {...lineReorder.handleProps(i)}
                              className="p-1 text-[#5B6B7C] hover:text-[#0088CC] cursor-grab active:cursor-grabbing"
                              title="Drag to reorder"
                              data-testid={`line-drag-${i}`}
                            >
                              <GripVertical className="h-4 w-4" />
                            </button>
                          </td>
                          <td colSpan={5} className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span
                                className="text-[10px] uppercase tracking-[0.25em] text-[#0088CC] font-display font-semibold px-2 py-0.5 rounded border border-[#0088CC]/40 bg-white"
                                data-testid={`line-heading-badge-${i}`}
                              >
                                Heading
                              </span>
                              <Input
                                value={li.description}
                                onChange={(e) => updateLine(i, { description: e.target.value })}
                                placeholder="MACHINERY"
                                className="flex-1 bg-white border-[#DDE5EC] text-[#011836] font-display font-bold uppercase tracking-wider h-9 focus-visible:ring-[#0088CC]"
                                data-testid={`line-heading-label-${i}`}
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {canEdit && (
                              <button
                                onClick={() => removeLine(i)}
                                className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC] transition-colors"
                                data-testid={`line-delete-${i}`}
                                aria-label="Remove heading"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    }
                    const amount = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
                    const amountHigh =
                      (Number(li.unit_price_high) || 0) > 0
                        ? (Number(li.quantity) || 0) * Number(li.unit_price_high)
                        : 0;
                    const subtotalBand = sectionSubtotalAfter[i];
                    return (
                      <Fragment key={li.id}>
                      <tr
                        {...lineReorder.rowProps(i)}
                        className={`border-b border-[#DDE5EC] align-top ${
                          isDragging ? "opacity-40" : ""
                        } ${isOver ? "ring-2 ring-[#0088CC]/60" : ""}`}
                        data-testid={`line-item-${i}`}
                      >
                        <td className="px-1 py-3 align-top">
                          <button
                            type="button"
                            {...lineReorder.handleProps(i)}
                            className="p-1 text-[#5B6B7C] hover:text-[#0088CC] cursor-grab active:cursor-grabbing"
                            title="Drag to reorder"
                            data-testid={`line-drag-${i}`}
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Textarea
                            value={li.description}
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                            placeholder="Line item description"
                            rows={1}
                            // Auto-grow: reset to auto then set to scrollHeight so
                            // the textarea always matches the wrapped content
                            // (no inner scrollbar). Runs on every keystroke.
                            onInput={(e) => {
                              e.target.style.height = "auto";
                              e.target.style.height = `${e.target.scrollHeight}px`;
                            }}
                            ref={(el) => {
                              // Also size correctly on initial mount (e.g. when
                              // loading an existing quote with long descriptions).
                              if (el && el.scrollHeight && el.style.height !== `${el.scrollHeight}px`) {
                                el.style.height = "auto";
                                el.style.height = `${el.scrollHeight}px`;
                              }
                            }}
                            className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] min-h-9 py-1.5 resize-none overflow-hidden"
                            data-testid={`line-desc-${i}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={li.quantity}
                            onChange={(e) => updateLine(i, { quantity: e.target.value })}
                            className="bg-white border-[#DDE5EC] text-[#011836] text-right h-9 focus-visible:ring-[#0088CC]"
                            data-testid={`line-qty-${i}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <NumberInputWithCommas
                            value={li.cost_price}
                            onChange={(e) => updateLine(i, { cost_price: e.target.value })}
                            className="bg-white border-[#DDE5EC] text-[#011836] text-right h-9 tabular-nums focus-visible:ring-[#0088CC]"
                            data-testid={`line-cost-${i}`}
                            title="Cost you pay per unit"
                          />
                          <NumberInputWithCommas
                            value={li.cost_price_high || ""}
                            onChange={(e) => updateLine(i, { cost_price_high: e.target.value })}
                            placeholder="to (est.)"
                            className="mt-1 bg-white border-[#DDE5EC]/70 text-[#5B6B7C] focus:text-[#011836] text-right h-7 text-xs tabular-nums focus-visible:ring-[#0088CC]"
                            data-testid={`line-cost-high-${i}`}
                            title="Optional high end — turns this line into an estimate range"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.5"
                            value={li.margin_pct}
                            onChange={(e) => updateLine(i, { margin_pct: e.target.value })}
                            className="bg-white border-[#DDE5EC] text-[#011836] text-right h-9 focus-visible:ring-[#0088CC]"
                            data-testid={`line-margin-${i}`}
                            title="Markup % — sell price = cost × (1 + markup/100)"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-display font-semibold text-[#011836] tabular-nums">
                          {formatMoney(amount, form.currency)}
                          {amountHigh > 0 && (
                            <div className="text-xs text-[#5B6B7C] font-normal">
                              – {formatMoney(amountHigh, form.currency)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {canEdit && (
                            <button
                              onClick={() => removeLine(i)}
                              className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC] transition-colors"
                              data-testid={`line-delete-${i}`}
                              aria-label="Remove line"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {subtotalBand && (
                        <tr
                          className="bg-white border-b-2 border-[#0088CC]/60"
                          data-testid={`line-section-subtotal-${i}`}
                        >
                          <td colSpan={5} className="px-3 py-1.5 text-right text-[10px] uppercase tracking-widest text-[#5B6B7C]">
                            {subtotalBand.label} subtotal
                          </td>
                          <td className="px-3 py-1.5 text-right font-display font-bold text-[#011836] tabular-nums">
                            {formatMoney(subtotalBand.amount, form.currency)}
                            {subtotalBand.amountHigh > 0 &&
                              ` – ${formatMoney(subtotalBand.amountHigh, form.currency)}`}
                          </td>
                          <td />
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {form.line_items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-sm text-[#5B6B7C]">
                        No line items. Click &ldquo;Add Item&rdquo; to begin.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="sos-surface rounded-lg p-5">
            <h2 className="font-display text-lg font-semibold text-[#011836] mb-3">Notes</h2>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              placeholder="Terms, scope notes, exclusions…"
              className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="quote-notes"
            />
          </section>

          {/* Proposal PDF content — intro letter, exclusions, optional
              per-quote overrides for Payment Terms / T&Cs / About SOS.
              Only rendered when editing an existing quote so the AI
              intro-letter endpoint has a persisted client/vessel to
              resolve. */}
          {!isNew && (
            <section className="sos-surface rounded-lg p-5" data-testid="proposal-section">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-lg font-semibold text-[#011836]">
                  Proposal Content
                </h2>
                <span className="text-[10px] uppercase tracking-widest text-[#5B6B7C]">
                  Renders in the exported PDF
                </span>
              </div>

              {/* Intro / covering letter with AI-generate + manual edit. */}
              <div className="border border-[#DDE5EC] rounded-md p-4 bg-white mb-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Introduction letter
                  </Label>
                  <div className="text-[10px] text-[#5B6B7C]">
                    Opens the PDF, addresses client by first name.
                  </div>
                </div>
                <Textarea
                  value={form.intro_letter}
                  onChange={(e) => setForm({ ...form, intro_letter: e.target.value })}
                  rows={6}
                  spellCheck
                  lang="en"
                  placeholder="Write a short covering letter — it opens the proposal and greets the client by first name."
                  className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="intro-letter-textarea"
                />
                <div className="flex items-center justify-between mt-1.5">
                  <div className="text-[10px] text-[#5B6B7C]">
                    {form.intro_letter
                      ? `${form.intro_letter.trim().split(/\s+/).length} words`
                      : "Empty — leave blank to skip the letter page."}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={runSpellCheck}
                    disabled={spellChecking || !(form.intro_letter || "").trim()}
                    className="border-[#C4D0DB] text-[#33475C] h-7 text-xs"
                    data-testid="spell-check-btn"
                  >
                    <SpellCheck className="h-3.5 w-3.5 mr-1.5" />
                    {spellChecking ? "Checking…" : "Check spelling"}
                  </Button>
                </div>
                {spellIssues && spellIssues.length > 0 && (
                  <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1.5" data-testid="spell-issues-panel">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">
                      {spellIssues.length} possible spelling issue{spellIssues.length !== 1 ? "s" : ""}
                    </div>
                    {spellIssues.map((iss) => (
                      <div key={iss.word} className="flex items-center flex-wrap gap-1.5 text-xs" data-testid={`spell-issue-${iss.word}`}>
                        <span className="text-red-400 font-semibold">{iss.word}</span>
                        <span className="text-[#64748B]">→</span>
                        {iss.suggestions.length === 0 && (
                          <span className="text-[#5B6B7C]">no suggestions</span>
                        )}
                        {iss.suggestions.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => fixSpelling(iss.word, s)}
                            className="px-2 py-0.5 rounded-full border border-[#0088CC]/50 text-[#0088CC] hover:bg-[#E6F2FA] hover:text-[#011836] transition-colors"
                            data-testid={`spell-fix-${iss.word}-${s}`}
                          >
                            {s}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setSpellIssues((x) => x.filter((y) => y.word !== iss.word))}
                          className="text-[#64748B] hover:text-[#011836] ml-1"
                          data-testid={`spell-ignore-${iss.word}`}
                        >
                          ignore
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {spellIssues && spellIssues.length === 0 && (
                  <div className="mt-1.5 text-[10px] text-emerald-400" data-testid="spell-clean-note">
                    No spelling issues found.
                  </div>
                )}
              </div>

              {/* Exclusion tick-boxes (from Settings library) + custom
                  free-text overflow. Both flow to the PDF. */}
              <div className="border border-[#DDE5EC] rounded-md p-4 bg-white mb-4">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C] mb-2 block">
                  Exclusions ({(form.exclusions_selected || []).length} of{" "}
                  {(settings?.exclusion_library || []).length} ticked
                  {form.exclusions_custom
                    ? ` · ${form.exclusions_custom.split(/\r?\n/).filter((l) => l.trim()).length} custom`
                    : ""})
                </Label>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 max-h-64 overflow-y-auto pr-1"
                  data-testid="exclusion-checkbox-list"
                >
                  {(settings?.exclusion_library || []).map((line, i) => {
                    const checked = (form.exclusions_selected || []).includes(line);
                    return (
                      <label
                        key={i}
                        className="flex items-start gap-2 text-sm text-[#011836] cursor-pointer group hover:bg-white rounded px-1.5 py-1"
                        data-testid={`exclusion-option-${i}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleExclusion(line)}
                          className="mt-0.5 h-4 w-4 accent-[#0088CC] shrink-0 cursor-pointer"
                        />
                        <span
                          className={
                            checked ? "text-[#011836]" : "text-[#5B6B7C] group-hover:text-[#011836]"
                          }
                        >
                          {line}
                        </span>
                      </label>
                    );
                  })}
                  {(settings?.exclusion_library || []).length === 0 && (
                    <div className="text-xs text-[#5B6B7C]">
                      No exclusions in the library. Add some in{" "}
                      <Link to="/settings" className="text-[#0088CC] hover:underline">
                        Settings
                      </Link>{" "}
                      to see tick-boxes here.
                    </div>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-[#DDE5EC]">
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Custom exclusions
                  </Label>
                  <Textarea
                    value={form.exclusions_custom}
                    onChange={(e) => setForm({ ...form, exclusions_custom: e.target.value })}
                    rows={3}
                    placeholder="One exclusion per line — appended below the ticked library items."
                    className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                    data-testid="exclusions-custom"
                  />
                </div>
              </div>

              {/* Attachments — original documents dropped into the deck
                  UNCHANGED (each PDF page becomes its own full page). */}
              <div className="border border-[#DDE5EC] rounded-md p-4 bg-white mb-4" data-testid="attachments-block">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Attachments ({(form.attachments || []).length})
                  </Label>
                  <label
                    className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-md border border-[#C4D0DB] text-[#33475C] text-xs cursor-pointer hover:bg-white ${attachUploading ? "opacity-50 pointer-events-none" : ""}`}
                    data-testid="attachment-upload-btn"
                  >
                    <Upload className="h-3 w-3" />
                    {attachUploading ? "Uploading…" : "Add document"}
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={(e) => {
                        addAttachment(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                      data-testid="attachment-file-input"
                    />
                  </label>
                </div>
                <div className="text-[10px] text-[#5B6B7C] mb-2.5">
                  Documents are included in the proposal exactly as uploaded — every PDF page
                  becomes its own full page at the slot you pick.
                </div>
                {(form.attachments || []).length === 0 ? (
                  <div className="text-xs text-[#64748B]">No attachments.</div>
                ) : (
                  <div className="space-y-2">
                    {form.attachments.map((a, i) => (
                      <div key={a.id || i} className="flex items-center gap-2">
                        <FileImage className="h-4 w-4 text-[#0088CC] shrink-0" />
                        <Input
                          value={a.title}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              attachments: f.attachments.map((y, yi) =>
                                yi === i ? { ...y, title: e.target.value } : y,
                              ),
                            }))
                          }
                          placeholder="Attachment title"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs flex-1"
                          data-testid={`attachment-title-${i}`}
                        />
                        <span className="text-[10px] text-[#64748B] whitespace-nowrap">
                          {(a.preview_paths || []).length > 0 ? `${a.preview_paths.length} page${a.preview_paths.length !== 1 ? "s" : ""}` : "1 page"}
                        </span>
                        <Select
                          value={a.position || "end"}
                          onValueChange={(v) =>
                            setForm((f) => ({
                              ...f,
                              attachments: f.attachments.map((y, yi) =>
                                yi === i ? { ...y, position: v } : y,
                              ),
                            }))
                          }
                        >
                          <SelectTrigger
                            className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs w-52"
                            data-testid={`attachment-position-${i}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                            <SelectItem value="after_cover">After cover page</SelectItem>
                            <SelectItem value="after_letter">After intro letter</SelectItem>
                            <SelectItem value="after_about">After about pages</SelectItem>
                            <SelectItem value="before_pricing">Before pricing</SelectItem>
                            <SelectItem value="after_pricing">After pricing</SelectItem>
                            <SelectItem value="before_ga">Before GA (after terms)</SelectItem>
                            <SelectItem value="end">End of document</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              attachments: f.attachments.filter((_, yi) => yi !== i),
                            }))
                          }
                          className="text-[#5B6B7C] hover:text-red-400 shrink-0"
                          data-testid={`attachment-remove-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Price options — mutually exclusive variants. The client
                  picks ONE on the proposal and its price replaces the
                  quote total. Heading rows group the options. */}
              <div className="border border-[#DDE5EC] rounded-md p-4 bg-white mb-4" data-testid="price-options-block">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Price Options ({(form.price_options || []).filter((o) => o.kind !== "heading").length})
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          price_options: [
                            ...(f.price_options || []),
                            {
                              kind: "option",
                              label: "",
                              description: "",
                              price: "",
                              is_default: !(f.price_options || []).some((o) => o.kind !== "heading"),
                            },
                          ],
                        }))
                      }
                      className="border-[#C4D0DB] text-[#33475C] h-7 text-xs"
                      data-testid="price-option-add"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add option
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          price_options: [
                            ...(f.price_options || []),
                            { kind: "heading", label: "", description: "", price: "", is_default: false },
                          ],
                        }))
                      }
                      className="border-[#C4D0DB] text-[#33475C] h-7 text-xs"
                      data-testid="price-option-add-heading"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add heading
                    </Button>
                  </div>
                </div>
                <div className="text-[10px] text-[#5B6B7C] mb-2.5">
                  Shown on the pricing page above the totals. The client selects ONE option and
                  its price replaces the quote total (GST and ticked extras still apply). Tick
                  the circle to set which option is pre-selected.
                </div>
                {(form.price_options || []).length === 0 ? (
                  <div className="text-xs text-[#64748B]">No price options.</div>
                ) : (
                  <div className="space-y-2">
                    {form.price_options.map((o, i) =>
                      o.kind === "heading" ? (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-[9px] uppercase tracking-widest text-[#64748B] w-14 shrink-0">Heading</span>
                          <Input
                            value={o.label}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                price_options: f.price_options.map((y, yi) =>
                                  yi === i ? { ...y, label: e.target.value } : y,
                                ),
                              }))
                            }
                            placeholder="e.g. Build location options"
                            className="bg-white border-[#DDE5EC] text-[#5B6B7C] h-8 text-xs flex-1 uppercase tracking-wider"
                            data-testid={`price-option-heading-input-${i}`}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                price_options: f.price_options.filter((_, yi) => yi !== i),
                              }))
                            }
                            className="text-[#5B6B7C] hover:text-red-400 shrink-0"
                            data-testid={`price-option-remove-${i}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={!!o.is_default}
                            onChange={() =>
                              setForm((f) => ({
                                ...f,
                                price_options: f.price_options.map((y, yi) => ({
                                  ...y,
                                  is_default: yi === i,
                                })),
                              }))
                            }
                            title="Pre-selected option"
                            className="h-3.5 w-3.5 accent-[#0088CC] shrink-0 cursor-pointer"
                            data-testid={`price-option-default-${i}`}
                          />
                          <Input
                            value={o.label}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                price_options: f.price_options.map((y, yi) =>
                                  yi === i ? { ...y, label: e.target.value } : y,
                                ),
                              }))
                            }
                            placeholder="e.g. Conventionally Powered (Vietnam)"
                            className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs flex-1"
                            data-testid={`price-option-label-${i}`}
                          />
                          <Input
                            value={o.description}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                price_options: f.price_options.map((y, yi) =>
                                  yi === i ? { ...y, description: e.target.value } : y,
                                ),
                              }))
                            }
                            placeholder="Short note (optional)"
                            className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs flex-1 hidden xl:block"
                            data-testid={`price-option-desc-${i}`}
                          />
                          <Input
                            value={o.price}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                price_options: f.price_options.map((y, yi) =>
                                  yi === i ? { ...y, price: e.target.value } : y,
                                ),
                              }))
                            }
                            type="number"
                            placeholder="Price"
                            className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs w-36 num"
                            data-testid={`price-option-price-${i}`}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                price_options: f.price_options.filter((_, yi) => yi !== i),
                              }))
                            }
                            className="text-[#5B6B7C] hover:text-red-400 shrink-0"
                            data-testid={`price-option-remove-${i}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>

              {/* Optional extras — listed on the proposal but never added
                  to the total. Clients tick them on the shared link to
                  preview an adjusted figure. */}
              <div className="border border-[#DDE5EC] rounded-md p-4 bg-white mb-4" data-testid="optional-extras-block">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Optional Extras ({(form.optional_extras || []).length})
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        optional_extras: [...(f.optional_extras || []), { description: "", cost: "", markup: "", price: "", qty: 1 }],
                      }))
                    }
                    className="border-[#C4D0DB] text-[#33475C] h-7 text-xs"
                    data-testid="optional-extra-add"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add extra
                  </Button>
                </div>
                <div className="text-[10px] text-[#5B6B7C] mb-2.5">
                  Shown under the pricing page — not included in the total. Clients see the
                  marked-up price only (cost + markup %); they can tick extras on the shared
                  proposal to preview the adjusted total.
                </div>
                {(form.optional_extras || []).length === 0 ? (
                  <div className="text-xs text-[#64748B]">No optional extras.</div>
                ) : (
                  <div className="space-y-2">
                    {form.optional_extras.map((x, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          value={x.description}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              optional_extras: f.optional_extras.map((y, yi) =>
                                yi === i ? { ...y, description: e.target.value } : y,
                              ),
                            }))
                          }
                          placeholder="e.g. Extended warranty package"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs flex-1"
                          data-testid={`optional-extra-desc-${i}`}
                        />
                        <Input
                          value={x.qty}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              optional_extras: f.optional_extras.map((y, yi) =>
                                yi === i ? { ...y, qty: e.target.value } : y,
                              ),
                            }))
                          }
                          type="number"
                          min="1"
                          placeholder="Qty"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs w-20 num"
                          data-testid={`optional-extra-qty-${i}`}
                        />
                        <Input
                          value={x.cost}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              optional_extras: f.optional_extras.map((y, yi) =>
                                yi === i ? { ...y, cost: e.target.value } : y,
                              ),
                            }))
                          }
                          type="number"
                          placeholder="Cost"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs w-28 num"
                          data-testid={`optional-extra-cost-${i}`}
                        />
                        <div className="flex items-center gap-1 shrink-0">
                          <Input
                            value={x.markup}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                optional_extras: f.optional_extras.map((y, yi) =>
                                  yi === i ? { ...y, markup: e.target.value } : y,
                                ),
                              }))
                            }
                            type="number"
                            placeholder="0"
                            className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-xs w-16 num"
                            data-testid={`optional-extra-markup-${i}`}
                          />
                          <span className="text-[10px] text-[#5B6B7C]">%</span>
                        </div>
                        <div
                          className="w-28 text-right text-xs font-semibold text-[#011836] tabular-nums shrink-0"
                          title="Client-facing price (cost + markup) × qty"
                          data-testid={`optional-extra-amount-${i}`}
                        >
                          {formatMoney(extraUnitPrice(x) * Math.max(1, Number(x.qty) || 1), form.currency)}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              optional_extras: f.optional_extras.filter((_, yi) => yi !== i),
                            }))
                          }
                          className="text-[#5B6B7C] hover:text-red-400 shrink-0"
                          data-testid={`optional-extra-remove-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* GA (General Arrangement) attachment. Uploads via the
                  shared /api/upload endpoint which auto-generates a PNG
                  preview for PDFs. The PDF export renders this on its
                  own final page; falls back to the linked vessel's GA
                  if this field is blank. */}
              <div className="border border-[#DDE5EC] rounded-md p-4 bg-white mb-4" data-testid="ga-upload-block">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    General Arrangement (GA)
                  </Label>
                  <div className="text-[10px] text-[#5B6B7C]">
                    Renders on the last PDF page.
                  </div>
                </div>
                {needsGaRegen && (
                  <div
                    className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200 flex items-start gap-2"
                    data-testid="ga-regen-banner"
                  >
                    <div className="flex-1 leading-relaxed">
                      <span className="font-semibold">Preview image missing.</span> The
                      PDF uploaded fine, but the page-1 thumbnail didn&apos;t generate
                      (usually a memory blip on smaller tiers). Regenerate the preview
                      to make it show on the printed proposal.
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={regenerateGaPreview}
                      disabled={regenGa}
                      className="bg-amber-500 hover:bg-amber-400 text-black text-xs shrink-0"
                      data-testid="ga-regen-btn"
                    >
                      {regenGa ? "Regenerating…" : "Regenerate"}
                    </Button>
                  </div>
                )}
                {form.ga_preview_path ? (
                  <div className="flex items-start gap-3">
                    <QuoteGaPreview path={form.ga_preview_path} />
                    <div className="flex flex-col gap-2">
                      {canEdit && (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => gaFileRef.current?.click()}
                            disabled={gaUploading}
                            className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836] text-xs"
                            data-testid="ga-replace-btn"
                          >
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {gaUploading ? "Uploading…" : "Replace"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={removeGa}
                            className="border-[#C4D0DB] text-red-400 hover:bg-red-500/10 hover:text-red-600 text-xs"
                            data-testid="ga-remove-btn"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ) : selectedVessel?.ga_preview_path ? (
                  <div className="flex items-start gap-3" data-testid="ga-vessel-fallback">
                    <QuoteGaPreview path={selectedVessel.ga_preview_path} />
                    <div className="flex flex-col gap-2 min-w-0">
                      <div className="text-xs text-[#10B981] leading-relaxed">
                        Using <span className="font-semibold">{selectedVessel.name}</span>&apos;s GA
                        automatically — it&apos;s already on the proposal.
                      </div>
                      {canEdit && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => gaFileRef.current?.click()}
                          disabled={gaUploading}
                          className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836] text-xs self-start"
                          data-testid="ga-override-btn"
                        >
                          <Upload className="h-3.5 w-3.5 mr-1" />
                          {gaUploading ? "Uploading…" : "Upload override"}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  canEdit && (
                    <div
                      className="border-2 border-dashed border-[#DDE5EC] rounded-md p-6 flex flex-col items-center justify-center text-center hover:border-[#0088CC]/60 transition-colors cursor-pointer"
                      onClick={() => gaFileRef.current?.click()}
                      data-testid="ga-upload-dropzone"
                    >
                      <Upload className="h-6 w-6 text-[#5B6B7C] mb-2" />
                      <div className="text-sm text-[#011836]">
                        {gaUploading ? "Uploading…" : "Click to upload a GA (PDF or image)"}
                      </div>
                      <div className="text-[10px] text-[#5B6B7C] mt-1">
                        {form.vessel_id
                          ? "Overrides the vessel's GA on this quote only."
                          : "No vessel selected — this GA will be used on the PDF."}
                      </div>
                    </div>
                  )
                )}
                <input
                  ref={gaFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  onChange={onGaFile}
                  className="hidden"
                  data-testid="ga-file-input"
                />
              </div>

              {/* Per-quote overrides. Leaving them blank uses the
                  Settings defaults — the field placeholder shows what
                  the default is so the admin can decide quickly. */}
              <details className="border border-[#DDE5EC] rounded-md bg-white">
                <summary className="cursor-pointer px-4 py-3 text-sm text-[#011836] flex items-center justify-between hover:bg-white rounded-md">
                  <span>Advanced: override Payment Terms / About / T&amp;Cs for this quote</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#5B6B7C]">
                    Optional
                  </span>
                </summary>
                <div className="p-4 space-y-3 border-t border-[#DDE5EC]">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Payment terms override
                    </Label>
                    <Textarea
                      value={form.payment_terms_override}
                      onChange={(e) =>
                        setForm({ ...form, payment_terms_override: e.target.value })
                      }
                      rows={4}
                      placeholder={settings?.default_payment_terms || "Uses Settings default"}
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                      data-testid="payment-terms-override"
                    />
                  </div>
                  <div
                    className="p-3 rounded border border-[#DDE5EC] bg-white space-y-3"
                    data-testid="vessel-options-section"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                          Vessel options — comparison page
                        </div>
                        <div className="text-xs text-[#5B6B7C] mt-0.5">
                          Present up to 4 vessels side by side (image, specs, price)
                          before the detailed pricing page.
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={(form.vessel_options || []).length >= 4}
                        onClick={addOption}
                        className="border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50 shrink-0"
                        data-testid="add-option-btn"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add option
                      </Button>
                    </div>
                    {(form.vessel_options || []).map((opt, oi) => (
                      <div
                        key={opt.id}
                        className="rounded border border-[#DDE5EC] bg-[#F6F7F8] p-3 space-y-2"
                        data-testid={`vessel-option-${oi}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-[#0088CC] shrink-0">
                            Option {oi + 1}
                          </span>
                          <div className="flex-1" />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeOption(oi)}
                            className="h-7 px-2 text-[#5B6B7C] hover:text-red-400"
                            data-testid={`remove-option-${oi}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Select
                            value={opt.vessel_id || ""}
                            onValueChange={(v) => optionPickVessel(oi, v)}
                          >
                            <SelectTrigger
                              className="bg-white border-[#DDE5EC] text-[#011836] h-9"
                              data-testid={`option-vessel-select-${oi}`}
                            >
                              <SelectValue placeholder="Select vessel" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                              {vessels.map((v) => (
                                <SelectItem key={v.id} value={v.id}>
                                  {v.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={opt.label}
                            onChange={(e) => updateOption(oi, { label: e.target.value })}
                            placeholder="Column label"
                            className="bg-white border-[#DDE5EC] text-[#011836] h-9"
                            data-testid={`option-label-${oi}`}
                          />
                          <NumberInputWithCommas
                            value={opt.price_override}
                            onChange={(e) => updateOption(oi, { price_override: e.target.value })}
                            placeholder={
                              vessels.find((v) => v.id === opt.vessel_id)?.price
                                ? `Vessel price: ${Number(
                                    vessels.find((v) => v.id === opt.vessel_id).price,
                                  ).toLocaleString()}`
                                : "Price (optional)"
                            }
                            className="bg-white border-[#DDE5EC] text-[#011836] h-9 text-right tabular-nums"
                            data-testid={`option-price-${oi}`}
                          />
                        </div>
                        {opt.vessel_id && (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                                Specs shown ({(opt.specs || []).length})
                              </span>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  updateOption(oi, { specs: [...(opt.specs || []), { label: "", value: "" }] })
                                }
                                className="h-6 px-2 text-[11px] text-[#5B6B7C] hover:text-[#0088CC]"
                                data-testid={`option-add-spec-${oi}`}
                              >
                                + Add row
                              </Button>
                            </div>
                            {(opt.specs || []).map((s, si) => (
                              <div key={si} className="flex gap-1.5 items-center">
                                <div className="flex flex-col shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (si === 0) return;
                                      const specs = [...opt.specs];
                                      [specs[si - 1], specs[si]] = [specs[si], specs[si - 1]];
                                      updateOption(oi, { specs });
                                    }}
                                    disabled={si === 0}
                                    className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-30"
                                    data-testid={`option-spec-up-${oi}-${si}`}
                                  >
                                    <ChevronUp className="h-3 w-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (si === opt.specs.length - 1) return;
                                      const specs = [...opt.specs];
                                      [specs[si], specs[si + 1]] = [specs[si + 1], specs[si]];
                                      updateOption(oi, { specs });
                                    }}
                                    disabled={si === opt.specs.length - 1}
                                    className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-30"
                                    data-testid={`option-spec-down-${oi}-${si}`}
                                  >
                                    <ChevronDown className="h-3 w-3" />
                                  </button>
                                </div>
                                <Input
                                  value={s.label}
                                  onChange={(e) =>
                                    updateOption(oi, {
                                      specs: opt.specs.map((x, xi) =>
                                        xi === si ? { ...x, label: e.target.value } : x,
                                      ),
                                    })
                                  }
                                  placeholder="Label"
                                  className="bg-white border-[#DDE5EC] text-[#33475C] h-7 text-xs flex-1"
                                />
                                <Input
                                  value={s.value}
                                  onChange={(e) =>
                                    updateOption(oi, {
                                      specs: opt.specs.map((x, xi) =>
                                        xi === si ? { ...x, value: e.target.value } : x,
                                      ),
                                    })
                                  }
                                  placeholder="Value"
                                  className="bg-white border-[#DDE5EC] text-[#33475C] h-7 text-xs flex-1"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateOption(oi, { specs: opt.specs.filter((_, xi) => xi !== si) })
                                  }
                                  className="p-1 text-[#5B6B7C] hover:text-red-400"
                                  data-testid={`option-remove-spec-${oi}-${si}`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {opt.vessel_id && (
                          <div className="mt-2">
                            <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                              Key features — one per line
                            </Label>
                            <Textarea
                              value={(opt.features || []).join("\n")}
                              onChange={(e) =>
                                updateOption(oi, { features: e.target.value.split("\n") })
                              }
                              rows={3}
                              placeholder={"e.g. 130 m³ freezer hold\nNozzle-ducted CPP propulsion\nFour-berth accommodation"}
                              className="mt-1 bg-white border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#0088CC]"
                              data-testid={`option-features-${oi}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div
                    className="p-3 rounded border border-[#DDE5EC] bg-white space-y-3"
                    data-testid="custom-pages-section"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                          Custom pages
                        </div>
                        <div className="text-xs text-[#5B6B7C] mt-0.5">
                          Insert your own pages, styled like the rest of the
                          proposal. Choose where each one appears.
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            custom_pages: [
                              ...(f.custom_pages || []),
                              {
                                id: Math.random().toString(36).slice(2),
                                title: "",
                                subtitle: "",
                                body: "",
                                position: "before_pricing",
                                content_blocks: [],
                              },
                            ],
                          }))
                        }
                        className="border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50 shrink-0"
                        data-testid="add-custom-page-btn"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add page
                      </Button>
                    </div>
                    {(form.custom_pages || []).map((pg, pi) => (
                      <div
                        key={pg.id}
                        className="rounded border border-[#DDE5EC] bg-[#F6F7F8] p-3 space-y-2"
                        data-testid={`custom-page-${pi}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-[#0088CC] shrink-0">
                            Page {pi + 1}
                          </span>
                          <div className="flex-1" />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                custom_pages: f.custom_pages.filter((_, idx) => idx !== pi),
                              }))
                            }
                            className="h-7 px-2 text-[#5B6B7C] hover:text-red-400"
                            data-testid={`remove-custom-page-${pi}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Input
                            value={pg.title}
                            onChange={(e) => updateCustomPage(pi, { title: e.target.value })}
                            placeholder="Page name (e.g. Warranty & Support)"
                            className="bg-white border-[#DDE5EC] text-[#011836] h-9"
                            data-testid={`custom-page-title-${pi}`}
                          />
                          <Input
                            value={pg.subtitle}
                            onChange={(e) => updateCustomPage(pi, { subtitle: e.target.value })}
                            placeholder="Subtitle (optional)"
                            className="bg-white border-[#DDE5EC] text-[#011836] h-9"
                            data-testid={`custom-page-subtitle-${pi}`}
                          />
                          <Select
                            value={pg.position || "before_pricing"}
                            onValueChange={(v) => updateCustomPage(pi, { position: v })}
                          >
                            <SelectTrigger
                              className="bg-white border-[#DDE5EC] text-[#011836] h-9"
                              data-testid={`custom-page-position-${pi}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                              <SelectItem value="after_cover">After cover page</SelectItem>
                              <SelectItem value="after_letter">After intro letter</SelectItem>
                              <SelectItem value="after_about">After about pages</SelectItem>
                              <SelectItem value="after_cover">After cover page</SelectItem>
                              <SelectItem value="after_letter">After intro letter</SelectItem>
                              <SelectItem value="after_about">After about pages</SelectItem>
                              <SelectItem value="before_pricing">Before pricing</SelectItem>
                              <SelectItem value="after_pricing">After pricing</SelectItem>
                              <SelectItem value="before_ga">Before GA (after terms)</SelectItem>
                              <SelectItem value="end">End of document (after GA)</SelectItem>
                              <SelectItem value="end">End of document</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Textarea
                          value={pg.body}
                          onChange={(e) => updateCustomPage(pi, { body: e.target.value })}
                          rows={5}
                          placeholder={"Page content. Leave a blank line between paragraphs.\n\nSingle line breaks are kept, so simple lists work too."}
                          className="bg-white border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#0088CC]"
                          data-testid={`custom-page-body-${pi}`}
                        />
                        {(pg.content_blocks || []).length > 0 && (
                          <div
                            className="rounded border border-[#DDE5EC] bg-white p-2 space-y-2"
                            data-testid={`custom-page-blocks-${pi}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] uppercase tracking-wider text-[#0088CC]">
                                Extracted content — rendered in proposal style
                              </span>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => updateCustomPage(pi, { content_blocks: [] })}
                                className="h-6 px-2 text-[10px] text-[#5B6B7C] hover:text-red-400"
                                data-testid={`custom-page-clear-blocks-${pi}`}
                              >
                                Clear all
                              </Button>
                            </div>
                            <div className="text-[9px] text-[#5B6B7C]">
                              Use **bold** or *italic* inside text · H toggles a line between
                              heading and text · ⤓ inserts a page break below
                            </div>
                            {pg.content_blocks.map((b, bi) => (
                              <div
                                key={bi}
                                className="flex items-start gap-1.5"
                                data-testid={`custom-page-block-${pi}-${bi}`}
                              >
                                <div className="flex flex-col shrink-0 pt-1">
                                  <button
                                    type="button"
                                    onClick={() => moveBlock(pi, bi, -1)}
                                    disabled={bi === 0}
                                    className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-30"
                                    data-testid={`custom-page-block-up-${pi}-${bi}`}
                                  >
                                    <ChevronUp className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveBlock(pi, bi, 1)}
                                    disabled={bi === pg.content_blocks.length - 1}
                                    className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-30"
                                    data-testid={`custom-page-block-down-${pi}-${bi}`}
                                  >
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <div className="flex-1 min-w-0">
                                  {b.type === "pagebreak" ? (
                                    <div className="flex items-center gap-2 h-7">
                                      <div className="flex-1 border-t border-dashed border-[#0088CC]/60" />
                                      <span className="text-[9px] uppercase tracking-[0.2em] text-[#0088CC]">
                                        Page break
                                      </span>
                                      <div className="flex-1 border-t border-dashed border-[#0088CC]/60" />
                                    </div>
                                  ) : b.type === "image" ? (
                                    <div className="flex items-center gap-2">
                                      <AuthedThumb
                                        path={b.path}
                                        className="h-12 w-16 object-contain rounded border border-[#DDE5EC] bg-white"
                                      />
                                      <span className="text-[10px] text-[#5B6B7C]">
                                        Image — shown at this point on the page
                                      </span>
                                    </div>
                                  ) : b.type === "table" ? (
                                    <div className="rounded border border-[#DDE5EC] bg-[#F6F7F8] p-1.5 space-y-1 overflow-x-auto">
                                      <div className="text-[9px] uppercase tracking-wider text-[#5B6B7C] px-0.5">
                                        Table — click any cell to edit · arrows move rows / columns
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <div className="w-3 shrink-0" />
                                        {((b.rows || [])[0] || []).map((_, ci) => (
                                          <div key={ci} className="flex-1 flex justify-center gap-2">
                                            <button
                                              type="button"
                                              disabled={ci === 0}
                                              onClick={() => {
                                                const rows = (b.rows || []).map((rr) => [...rr]);
                                                for (const r of rows) {
                                                  const t = r[ci];
                                                  r[ci] = r[ci - 1] ?? "";
                                                  r[ci - 1] = t ?? "";
                                                }
                                                updateBlock(pi, bi, { rows });
                                              }}
                                              className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-25"
                                              title="Move column left"
                                              data-testid={`custom-page-table-col-left-${pi}-${bi}-${ci}`}
                                            >
                                              <ChevronLeft className="h-3 w-3" />
                                            </button>
                                            <button
                                              type="button"
                                              disabled={ci === ((b.rows || [])[0] || []).length - 1}
                                              onClick={() => {
                                                const rows = (b.rows || []).map((rr) => [...rr]);
                                                for (const r of rows) {
                                                  const t = r[ci];
                                                  r[ci] = r[ci + 1] ?? "";
                                                  r[ci + 1] = t ?? "";
                                                }
                                                updateBlock(pi, bi, { rows });
                                              }}
                                              className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-25"
                                              title="Move column right"
                                              data-testid={`custom-page-table-col-right-${pi}-${bi}-${ci}`}
                                            >
                                              <ChevronRight className="h-3 w-3" />
                                            </button>
                                          </div>
                                        ))}
                                        <div className="w-6 shrink-0" />
                                      </div>
                                      {(b.rows || []).map((r, ri) => (
                                        <div key={ri} className="flex items-center gap-1">
                                          <div className="flex flex-col w-3 shrink-0">
                                            {ri > 0 && (
                                              <>
                                                <button
                                                  type="button"
                                                  disabled={ri === 1}
                                                  onClick={() => {
                                                    const rows = (b.rows || []).map((rr) => [...rr]);
                                                    [rows[ri - 1], rows[ri]] = [rows[ri], rows[ri - 1]];
                                                    updateBlock(pi, bi, { rows });
                                                  }}
                                                  className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-25"
                                                  title="Move row up"
                                                  data-testid={`custom-page-table-row-up-${pi}-${bi}-${ri}`}
                                                >
                                                  <ChevronUp className="h-3 w-3" />
                                                </button>
                                                <button
                                                  type="button"
                                                  disabled={ri === (b.rows || []).length - 1}
                                                  onClick={() => {
                                                    const rows = (b.rows || []).map((rr) => [...rr]);
                                                    [rows[ri], rows[ri + 1]] = [rows[ri + 1], rows[ri]];
                                                    updateBlock(pi, bi, { rows });
                                                  }}
                                                  className="text-[#5B6B7C] hover:text-[#0088CC] disabled:opacity-25"
                                                  title="Move row down"
                                                  data-testid={`custom-page-table-row-down-${pi}-${bi}-${ri}`}
                                                >
                                                  <ChevronDown className="h-3 w-3" />
                                                </button>
                                              </>
                                            )}
                                          </div>
                                          {r.map((c, ci) => (
                                            <Input
                                              key={ci}
                                              value={c || ""}
                                              onChange={(e) => {
                                                const rows = (b.rows || []).map((rr) => [...rr]);
                                                rows[ri][ci] = e.target.value;
                                                updateBlock(pi, bi, { rows });
                                              }}
                                              className={`bg-white border-[#DDE5EC] text-[#011836] h-7 text-[10px] px-1.5 flex-1 ${
                                                ri === 0 ? "font-semibold text-[#0088CC]" : ""
                                              } ${ci === 0 ? "min-w-[110px]" : "min-w-[60px]"}`}
                                              data-testid={`custom-page-table-cell-${pi}-${bi}-${ri}-${ci}`}
                                            />
                                          ))}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const rows = (b.rows || []).filter(
                                                (_, i) => i !== ri,
                                              );
                                              updateBlock(pi, bi, { rows });
                                            }}
                                            className="text-[#5B6B7C] hover:text-red-400 shrink-0"
                                            title="Remove row"
                                            data-testid={`custom-page-table-row-remove-${pi}-${bi}-${ri}`}
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : b.type === "heading" ? (
                                    <Input
                                      value={b.text || ""}
                                      onChange={(e) => updateBlock(pi, bi, { text: e.target.value })}
                                      placeholder="Section heading"
                                      className="bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] h-8 text-xs font-semibold"
                                      data-testid={`custom-page-block-text-${pi}-${bi}`}
                                    />
                                  ) : (
                                    <Textarea
                                      value={b.text || ""}
                                      onChange={(e) => updateBlock(pi, bi, { text: e.target.value })}
                                      rows={Math.min(6, Math.max(2, Math.ceil((b.text || "").length / 90)))}
                                      className="bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#0088CC]"
                                      data-testid={`custom-page-block-text-${pi}-${bi}`}
                                    />
                                  )}
                                </div>
                                <div className="flex items-center shrink-0 gap-0.5">
                                  {(b.type === "text" || b.type === "heading") && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateBlock(pi, bi, {
                                          type: b.type === "heading" ? "text" : "heading",
                                        })
                                      }
                                      title={b.type === "heading" ? "Change to text" : "Make heading (bold)"}
                                      className={`p-1 rounded ${
                                        b.type === "heading"
                                          ? "text-[#0088CC] bg-[#0088CC]/10"
                                          : "text-[#5B6B7C] hover:text-[#0088CC]"
                                      }`}
                                      data-testid={`custom-page-block-toggle-${pi}-${bi}`}
                                    >
                                      <HeadingIcon className="h-3 w-3" />
                                    </button>
                                  )}
                                  {b.type !== "pagebreak" && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateCustomPage(pi, {
                                          content_blocks: [
                                            ...pg.content_blocks.slice(0, bi + 1),
                                            { type: "pagebreak" },
                                            ...pg.content_blocks.slice(bi + 1),
                                          ],
                                        })
                                      }
                                      title="Insert page break below"
                                      className="p-1 text-[#5B6B7C] hover:text-[#0088CC]"
                                      data-testid={`custom-page-block-break-${pi}-${bi}`}
                                    >
                                      <SeparatorHorizontal className="h-3 w-3" />
                                    </button>
                                  )}
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeBlock(pi, bi)}
                                    className="h-7 px-1.5 text-[#5B6B7C] hover:text-red-400"
                                    data-testid={`custom-page-block-remove-${pi}-${bi}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                updateCustomPage(pi, {
                                  content_blocks: [
                                    ...pg.content_blocks,
                                    { type: "text", text: "" },
                                  ],
                                })
                              }
                              className="border-[#C4D0DB] text-[#33475C] h-7 text-[10px] hover:text-[#0088CC] hover:border-[#0088CC]/50"
                              data-testid={`custom-page-add-block-${pi}`}
                            >
                              <Plus className="h-3 w-3 mr-1" /> Add text block
                            </Button>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          {pg.image_preview_path || pg.image_path ? (
                            <>
                              <AuthedThumb
                                path={pg.image_preview_path || pg.image_path}
                                className="h-14 w-20 object-cover rounded border border-[#DDE5EC]"
                              />
                              <span className="text-xs text-[#5B6B7C] flex-1 truncate">
                                {(pg.image_preview_paths || []).length > 1
                                  ? `${pg.image_preview_paths.length} pages attached — each shown on its own page`
                                  : "Artwork attached — shown under the heading on the PDF"}
                              </span>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  updateCustomPage(pi, {
                                    image_path: "",
                                    image_preview_path: "",
                                    image_preview_paths: [],
                                  })
                                }
                                className="h-7 px-2 text-[#5B6B7C] hover:text-red-400"
                                data-testid={`custom-page-remove-image-${pi}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={customPageUploading}
                              onClick={() => {
                                customPageUploadIdx.current = pi;
                                customPageFileRef.current?.click();
                              }}
                              className="border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50"
                              data-testid={`custom-page-upload-${pi}`}
                            >
                              <Upload className="h-3.5 w-3.5 mr-1" />
                              {customPageUploading
                                ? "Extracting…"
                                : (pg.content_blocks || []).length > 0
                                  ? "Replace with new document"
                                  : "Upload document (PDF / Word) / image"}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    <input
                      ref={customPageFileRef}
                      type="file"
                      accept="image/*,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={onCustomPageFile}
                      className="hidden"
                      data-testid="custom-page-file-input"
                    />
                  </div>
                  <div
                    className="p-3 rounded border border-[#DDE5EC] bg-white space-y-2"
                    data-testid="cover-image-section"
                  >
                    <div className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Cover image override
                    </div>
                    <div className="text-xs text-[#5B6B7C]">
                      Replaces the vessel render on the cover page for this quote only.
                      Leave empty to use the linked vessel's render.
                    </div>
                    <label
                      className={`flex items-center justify-between gap-3 p-2.5 rounded border cursor-pointer ${
                        form.cover_no_image
                          ? "border-[#0088CC]/40 bg-[#0088CC]/[0.06]"
                          : "border-[#DDE5EC] bg-white"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-[#011836] font-medium">No cover image</div>
                        <div className="text-xs text-[#5B6B7C] mt-0.5">
                          Plain dark-blue cover background — no render or uploaded image.
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={!!form.cover_no_image}
                        onChange={(e) => setForm({ ...form, cover_no_image: e.target.checked })}
                        className="h-5 w-5 rounded border-[#C4D0DB] bg-white text-[#0088CC] focus:ring-2 focus:ring-[#0088CC]/60 cursor-pointer shrink-0"
                        data-testid="cover-no-image-checkbox"
                      />
                    </label>
                    {!form.cover_no_image && (
                      <>
                    {form.cover_image_path && (
                      <div className="flex items-center gap-3">
                        <QuoteGaPreview path={form.cover_image_path} />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setForm((f) => ({ ...f, cover_image_path: "" }))}
                          className="border-[#C4D0DB] text-red-600 hover:text-red-700 hover:border-red-300"
                          data-testid="cover-image-remove-btn"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove — use vessel render
                        </Button>
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={coverUploading}
                      onClick={() => coverFileRef.current?.click()}
                      className="border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50"
                      data-testid="cover-image-upload-btn"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" />
                      {coverUploading
                        ? "Uploading…"
                        : form.cover_image_path
                          ? "Replace cover image"
                          : "Upload cover image"}
                    </Button>
                      </>
                    )}
                    <input
                      ref={coverFileRef}
                      type="file"
                      accept="image/*"
                      onChange={onCoverFile}
                      className="hidden"
                      data-testid="cover-image-file-input"
                    />
                  </div>
                  <div
                    className="p-3 rounded border border-[#DDE5EC] bg-white space-y-2"
                    data-testid="proposal-pages-section"
                  >
                    <div className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Proposal pages
                    </div>
                    <div className="text-xs text-[#5B6B7C]">
                      Every page of the deck in print order — untick any you don't
                      want on this proposal. The cover page is always included.
                    </div>
                    {[
                      {
                        key: "include_letter_page",
                        label: "Introduction letter",
                        hint: "The covering letter page. Only appears when a letter is written above.",
                        testid: "letter-page-checkbox",
                      },
                      {
                        key: "include_about_sos",
                        label: "About SOS",
                        hint: "The standard company page (uses the override below or the Settings default).",
                        testid: "about-sos-page-checkbox",
                      },
                      {
                        key: "include_yachts_page",
                        label: "Introducing Southern Ocean Yachts",
                        hint: "Yacht-focused page — Beyond Series hero image + Southern Ocean Yachts copy.",
                        testid: "about-yachts-checkbox",
                      },
                      {
                        key: "include_shipyard_page",
                        label: "About Our Shipyard",
                        hint: "Haiphong shipyard page — photos, capabilities, BV/DNV accreditation, address.",
                        testid: "shipyard-page-checkbox",
                      },
                      {
                        key: "include_options_page",
                        label: "Vessel options page",
                        hint: "Side-by-side vessel comparison — only appears when options are added.",
                        testid: "options-page-checkbox",
                      },
                      {
                        key: "include_project_page",
                        label: "The Project",
                        hint: "Vessel overview page with specs — only appears when a vessel is linked.",
                        testid: "project-page-checkbox",
                      },
                      {
                        key: "include_pricing_page",
                        label: "Pricing page",
                        hint: "The quote table with line items, options and totals.",
                        testid: "pricing-page-checkbox",
                      },
                      {
                        key: "include_details_page",
                        label: "Details page",
                        hint: "Payment terms, terms & conditions and exclusions.",
                        testid: "details-page-checkbox",
                      },
                      {
                        key: "include_renders_page",
                        label: "Vessel renders page",
                        hint: "Grid of up to 8 renders from the vessel's page, placed just before the GA drawing. Skipped if the vessel has no renders uploaded.",
                        testid: "renders-page-checkbox",
                      },
                      {
                        key: "include_ga_page",
                        label: "GA drawing page(s)",
                        hint: "The General Arrangement drawing — plus one GA page per option vessel.",
                        testid: "ga-page-checkbox",
                      },
                    ].map((p) => (
                      <label
                        key={p.key}
                        className={`flex items-center justify-between gap-3 p-2.5 rounded border cursor-pointer ${
                          form[p.key]
                            ? "border-[#0088CC]/40 bg-[#0088CC]/[0.06]"
                            : "border-[#DDE5EC] bg-white"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-[#011836] font-medium">{p.label}</div>
                          <div className="text-xs text-[#5B6B7C] mt-0.5">{p.hint}</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={!!form[p.key]}
                          onChange={(e) => setForm({ ...form, [p.key]: e.target.checked })}
                          className="h-5 w-5 rounded border-[#C4D0DB] bg-white text-[#0088CC] focus:ring-2 focus:ring-[#0088CC]/60 cursor-pointer shrink-0"
                          data-testid={p.testid}
                        />
                      </label>
                    ))}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      About SOS override
                    </Label>
                    <Textarea
                      value={form.about_sos_override}
                      onChange={(e) =>
                        setForm({ ...form, about_sos_override: e.target.value })
                      }
                      rows={3}
                      placeholder={settings?.default_about_sos || "Uses Settings default"}
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                      data-testid="about-sos-override"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Terms &amp; conditions override
                    </Label>
                    <Textarea
                      value={form.terms_override}
                      onChange={(e) => setForm({ ...form, terms_override: e.target.value })}
                      rows={5}
                      placeholder={settings?.default_terms_conditions || "Uses Settings default"}
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                      data-testid="terms-override"
                    />
                  </div>
                </div>
              </details>
            </section>
          )}
        </div>

        {/* Right: totals */}
        <aside className="space-y-6 lg:sticky lg:top-6 self-start">
          <div className="sos-surface rounded-lg p-5">
            <h2 className="font-display text-lg font-semibold text-[#011836] mb-4">Totals</h2>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                GST {form.gst_enabled ? `(${Math.round((form.gst_rate || 0) * 100)}%)` : ""}
              </Label>
              <Switch
                checked={form.gst_enabled}
                onCheckedChange={(v) => setForm({ ...form, gst_enabled: v })}
                data-testid="gst-toggle"
              />
            </div>
            {form.gst_enabled && (
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  GST rate (0.10 = 10%)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.gst_rate}
                  onChange={(e) => setForm({ ...form, gst_rate: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="gst-rate-input"
                />
              </div>
            )}

            <div className="mb-4" data-testid="alt-currencies-section">
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Also show prices in
              </Label>
              {["AUD", "USD", "NZD", "EUR"]
                .filter((c) => c !== form.currency)
                .map((c) => {
                  const entry = (form.alt_currencies || []).find((a) => a.currency === c);
                  return (
                    <div key={c} className="mt-2 flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm text-[#011836] cursor-pointer select-none w-14 shrink-0">
                        <input
                          type="checkbox"
                          checked={!!entry}
                          onChange={(e) => {
                            const list = (form.alt_currencies || []).filter(
                              (a) => a.currency !== c,
                            );
                            if (e.target.checked) list.push({ currency: c, rate: "" });
                            setForm({ ...form, alt_currencies: list });
                            if (e.target.checked) applyLiveRate(c);
                          }}
                          className="accent-[#0088CC] h-3.5 w-3.5"
                          data-testid={`alt-currency-toggle-${c}`}
                        />
                        {c}
                      </label>
                      {entry && (
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <span className="text-[10px] text-[#5B6B7C] whitespace-nowrap">
                            1 {form.currency} =
                          </span>
                          <Input
                            type="number"
                            step="0.0001"
                            placeholder="rate"
                            value={entry.rate}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                alt_currencies: form.alt_currencies.map((a) =>
                                  a.currency === c ? { ...a, rate: e.target.value } : a,
                                ),
                              })
                            }
                            className="h-7 bg-white border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#0088CC]"
                            data-testid={`alt-currency-rate-${c}`}
                          />
                          <button
                            type="button"
                            title="Refresh with the live rate"
                            disabled={fxLoading}
                            onClick={() => applyLiveRate(c)}
                            className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded border border-[#DDE5EC] text-[#5B6B7C] hover:text-[#0088CC] hover:border-[#0088CC]/50 disabled:opacity-50 transition-colors"
                            data-testid={`alt-currency-refresh-${c}`}
                          >
                            <RefreshCw className={`h-3 w-3 ${fxLoading ? "animate-spin" : ""}`} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              {(form.alt_currencies || []).length > 0 && (
                <div className="mt-1.5 text-[10px] text-[#5B6B7C]" data-testid="fx-rate-date-note">
                  {fxData?.rate_date
                    ? `Live rates: ECB reference, ${fxData.rate_date} — editable.`
                    : "Rates auto-fill from live ECB data when ticked — editable."}
                </div>
              )}
            </div>

            <div className="border-t border-[#DDE5EC] pt-4 space-y-2">
              {totals.hasRange ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5B6B7C]">Estimated range</span>
                    <span className="font-display text-[#011836] tabular-nums" data-testid="quote-subtotal">
                      {formatMoney(totals.subtotal, form.currency)} – {formatMoney(totals.subtotalHigh, form.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-[#DDE5EC]">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#0088CC]">
                      Estimate
                    </span>
                    <span
                      className="font-display text-lg font-bold text-[#011836] tabular-nums text-right"
                      data-testid="quote-total"
                    >
                      {formatMoney(totals.subtotal, form.currency)}
                      <br />– {formatMoney(totals.subtotalHigh, form.currency)}
                    </span>
                  </div>
                  <div className="text-right text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Excl. GST — shown as a range on the PDF
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5B6B7C]">Subtotal</span>
                    <span className="font-display text-[#011836] tabular-nums" data-testid="quote-subtotal">
                      {formatMoney(totals.subtotal, form.currency)}
                    </span>
                  </div>
                  {form.gst_enabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#5B6B7C]">GST</span>
                      <span className="font-display text-[#011836] tabular-nums" data-testid="quote-gst">
                        {formatMoney(totals.gst, form.currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-[#DDE5EC]">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#0088CC]">Total</span>
                    <span
                      className="font-display text-2xl font-bold text-[#011836] tabular-nums"
                      data-testid="quote-total"
                    >
                      {formatMoney(totals.total, form.currency)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent
          className="bg-white border-[#DDE5EC] text-[#011836] max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="catalogue-picker-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <Package className="inline-block h-4 w-4 mr-2 text-[#0088CC]" />
              Catalogue
            </DialogTitle>
          </DialogHeader>
          <div className="relative">
            <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B6B7C]" />
            <Input
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder="Search products by name, SKU, supplier…"
              className="pl-9 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="catalogue-search-input"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto -mx-6 px-6 pt-3 pb-2 flex-1">
            {filteredProducts.length === 0 && (
              <div className="py-12 text-center text-sm text-[#5B6B7C]">
                {products.length === 0
                  ? "No products in catalogue yet. Open the Products page to add some."
                  : "No products match your search."}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredProducts.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    if ((p.spec_sheets || []).length > 0) {
                      setSpecPick({
                        product: p,
                        checked: Object.fromEntries(p.spec_sheets.map((_, i) => [i, true])),
                      });
                    } else {
                      addFromProduct(p);
                      setPickerOpen(false);
                    }
                  }}
                  className="text-left sos-surface sos-surface-hover rounded-md overflow-hidden flex gap-3 p-3"
                  data-testid={`picker-product-${p.id}`}
                >
                  <div className="h-16 w-16 rounded bg-white border border-[#DDE5EC] overflow-hidden flex items-center justify-center shrink-0 relative">
                    {p.image_path ? (
                      <img
                        src={imageSrc(p.image_path)}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-[#B6C2CD]" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display text-sm font-semibold text-[#011836] truncate">
                        {p.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-[#0088CC] shrink-0">
                        {p.category}
                      </div>
                    </div>
                    <div className="text-xs text-[#5B6B7C] mt-0.5">
                      {p.sku ? `SKU ${p.sku} · ` : ""}
                      {p.supplier_name || "—"}
                    </div>
                    <div className="text-sm text-[#011836] font-display font-semibold mt-1 tabular-nums">
                      {formatMoney(p.price, p.currency)}
                      <span className="text-[#5B6B7C] font-normal text-xs"> / {p.unit}</span>
                    </div>
                    {(p.spec_sheets || []).length > 0 && (
                      <div className="text-[10px] text-[#0088CC] mt-1 flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {p.spec_sheets.length} spec sheet{p.spec_sheets.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Spec-sheet chooser — shown when a picked product carries sheets */}
      <Dialog open={!!specPick} onOpenChange={(o) => !o && !specAdding && setSpecPick(null)}>
        <DialogContent className="bg-white border-[#DDE5EC] text-[#011836] max-w-md" data-testid="spec-sheet-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              <FileText className="inline-block h-4 w-4 mr-2 text-[#0088CC]" />
              Include spec sheets?
            </DialogTitle>
          </DialogHeader>
          {specPick && (
            <>
              <p className="text-sm text-[#5B6B7C] -mt-1">
                <span className="font-semibold text-[#011836]">{specPick.product.name}</span> has{" "}
                {specPick.product.spec_sheets.length} spec sheet
                {specPick.product.spec_sheets.length > 1 ? "s" : ""}. Tick the ones to attach to this
                quote's proposal:
              </p>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {specPick.product.spec_sheets.map((s, i) => (
                  <label
                    key={i}
                    className="flex items-center gap-2.5 rounded-md border border-[#DDE5EC] bg-white px-3 py-2.5 cursor-pointer hover:bg-[#F4FAFD]"
                    data-testid={`spec-sheet-option-${i}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!specPick.checked[i]}
                      onChange={(e) =>
                        setSpecPick((sp) => ({
                          ...sp,
                          checked: { ...sp.checked, [i]: e.target.checked },
                        }))
                      }
                      className="h-4 w-4 accent-[#0088CC]"
                    />
                    <span className="text-sm text-[#011836] truncate">{s.name || `Spec sheet ${i + 1}`}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={specAdding}
                  onClick={() => confirmSpecPick(false)}
                  className="border-[#CBD5E1] bg-white text-[#334155] hover:bg-[#F1F5F9]"
                  data-testid="spec-sheet-skip-btn"
                >
                  Item only
                </Button>
                <Button
                  size="sm"
                  disabled={specAdding || !Object.values(specPick.checked).some(Boolean)}
                  onClick={() => confirmSpecPick(true)}
                  className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                  data-testid="spec-sheet-add-btn"
                >
                  {specAdding
                    ? "Attaching…"
                    : `Add with ${Object.values(specPick.checked).filter(Boolean).length} sheet${Object.values(specPick.checked).filter(Boolean).length !== 1 ? "s" : ""}`}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {!isNew && (
        <QuoteVersionsDrawer
          quoteId={id}
          open={versionsOpen}
          onOpenChange={setVersionsOpen}
          canEdit={canEdit}
          onRestored={(fresh) => {
            // Push restored data back into the editor so the UI reflects
            // the reverted state immediately without a full reload.
            setForm((f) => ({
              ...f,
              title: fresh.title || "",
              client_id: fresh.client_id || null,
              vessel_id: fresh.vessel_id || null,
              currency: fresh.currency || "AUD",
              gst_enabled: !!fresh.gst_enabled,
              gst_rate: fresh.gst_rate ?? 0.10,
              status: fresh.status || "draft",
              alt_currencies: fresh.alt_currencies || [],
              sent_at: fresh.sent_at || null,
              last_emailed_at: fresh.last_emailed_at || null,
              last_emailed_to: fresh.last_emailed_to || [],
              last_viewed_at: fresh.last_viewed_at || null,
              view_events: fresh.view_events || [],
              notes: fresh.notes || "",
              valid_until: fresh.valid_until || "",
              line_items: (fresh.line_items || []).map((li) => ({
                kind: li.kind === "heading" ? "heading" : "item",
                cost_price: li.cost_price ?? 0,
                margin_pct: li.margin_pct ?? 15,
                cost_price_high: li.cost_price_high ?? 0,
                unit_price_high: li.unit_price_high ?? 0,
                ...li,
              })),
              number: fresh.number || f.number,
              intro_letter: fresh.intro_letter || "",
              payment_terms_override: fresh.payment_terms_override || "",
              terms_override: fresh.terms_override || "",
              about_sos_override: fresh.about_sos_override || "",
              about_variant: fresh.about_variant || "sos",
              include_about_sos: fresh.include_about_sos ?? fresh.about_variant !== "yachts",
              include_yachts_page: fresh.include_yachts_page ?? fresh.about_variant === "yachts",
              include_shipyard_page: !!fresh.include_shipyard_page,
              include_renders_page: !!fresh.include_renders_page,
              include_letter_page: fresh.include_letter_page !== false,
              include_options_page: fresh.include_options_page !== false,
              include_project_page: fresh.include_project_page !== false,
              include_pricing_page: fresh.include_pricing_page !== false,
              include_details_page: fresh.include_details_page !== false,
              include_ga_page: fresh.include_ga_page !== false,
              cover_image_path: fresh.cover_image_path || "",
              cover_no_image: !!fresh.cover_no_image,
              vessel_options: (fresh.vessel_options || []).map((o) => ({
                id: o.id || Math.random().toString(36).slice(2),
                vessel_id: o.vessel_id || "",
                label: o.label || "",
                price_override: o.price_override ?? "",
                specs: o.specs || [],
                features: o.features || [],
              })),
              custom_pages: (fresh.custom_pages || []).map((p) => ({
                id: p.id || Math.random().toString(36).slice(2),
                title: p.title || "",
                subtitle: p.subtitle || "",
                body: p.body || "",
                position: p.position || "before_pricing",
                image_path: p.image_path || "",
                image_preview_path: p.image_preview_path || "",
                image_preview_paths: p.image_preview_paths || [],
                content_blocks: p.content_blocks || [],
              })),
              exclusions_selected: fresh.exclusions_selected || [],
              exclusions_custom: fresh.exclusions_custom || "",
              optional_extras: (fresh.optional_extras || []).map((x) => ({
                description: x.description || "",
                cost: x.cost ?? x.price ?? "",
                markup: x.markup ?? "",
                price: x.price ?? "",
                qty: x.qty ?? 1,
              })),
              ga_path: fresh.ga_path || "",
              ga_preview_path: fresh.ga_preview_path || "",
            }));
            // Restore appends two new snapshots (pre-restore save + restore
            // tag). Refresh the header badge so it doesn't stay stale.
            refreshVersionCount();
          }}
        />
      )}

      {!isNew && (
        <EmailProposalDialog
          open={emailOpen}
          onOpenChange={setEmailOpen}
          quote={{
            id,
            number: form.number,
            title: form.title,
            intro_letter: form.intro_letter,
            recipient_links: form.recipient_links || [],
            view_events: form.view_events || [],
            vessel: vessels.find((v) => v.id === form.vessel_id) || null,
          }}
          client={(() => {
            const c = clients.find((x) => x.id === form.client_id) || null;
            if (!c || !form.attention_contact_id) return c;
            const chosen = (c.contacts || []).find((ct) => ct.id === form.attention_contact_id);
            return chosen
              ? { ...c, primary_contact: chosen, primary_contact_id: chosen.id, email: chosen.email || "", phone: chosen.phone || "" }
              : c;
          })()}
          settings={settings}
          onSent={(fresh) => {
            if (!fresh) return;
            // Sync the pipeline pill and tracking data with the server.
            setForm((f) => ({
              ...f,
              status: fresh.status || f.status,
              recipient_links: fresh.recipient_links || f.recipient_links || [],
              view_events: fresh.view_events || f.view_events || [],
              last_emailed_at: fresh.last_emailed_at || f.last_emailed_at,
            }));
            refreshVersionCount();
          }}
        />
      )}
      {!isNew && (
        <ShareLinkDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          quoteId={id}
          canEdit={canEdit}
        />
      )}

      {!isNew && (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent
            className="bg-white border-[#C4D0DB] text-[#011836]"
            data-testid="delete-quote-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#011836]">Delete this quote?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#5B6B7C]">
                {form.number ? (
                  <>
                    <span className="text-[#011836] font-semibold">{form.number}</span>
                    {form.title ? ` — ${form.title}` : ""} will be permanently removed
                    along with its version history and any share link. This can&apos;t be
                    undone.
                  </>
                ) : (
                  <>This quote will be permanently removed along with its version history and any share link. This can&apos;t be undone.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={deleting}
                className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                data-testid="delete-quote-cancel"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  deleteQuote();
                }}
                disabled={deleting}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                data-testid="delete-quote-confirm"
              >
                {deleting ? "Deleting…" : "Delete quote"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

// Small authed thumbnail used inside the GA upload block. `useAuthedImage`
// handles the token-bearing fetch + blob caching so the preview renders
// through the RBAC-protected /api/files endpoint.
function AuthedThumb({ path, className }) {
  const { src } = useAuthedImage(path);
  if (!src) return <div className={`${className} bg-white animate-pulse`} />;
  return <img src={src} alt="attachment" className={className} />;
}

function QuoteGaPreview({ path }) {
  const { src, loading } = useAuthedImage(path);
  if (loading) {
    return (
      <div className="w-40 h-28 rounded border border-[#DDE5EC] bg-white animate-pulse" />
    );
  }
  if (!src) {
    return (
      <div className="w-40 h-28 rounded border border-[#DDE5EC] bg-white flex items-center justify-center text-[10px] text-[#5B6B7C] text-center px-2">
        Preview unavailable
      </div>
    );
  }
  return (
    <img
      src={src}
      alt="GA preview"
      className="w-40 h-28 rounded border border-[#DDE5EC] object-contain bg-white"
      data-testid="ga-preview-image"
    />
  );
}
